import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from './CartState';
import { useCurrency } from './CurrencyState';
import { useSensors, useSensor, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';

import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Editable from '../components/Editable.jsx';
import GlobalStyles from '../components/GlobalStyles';
import SeoHead from '../components/SeoHead';
import FloatingAdminBar from '../components/FloatingAdminBar';
import HeroBlock from '../components/HeroBlock.jsx';
import { BrandEditable } from '../components/BrandEditable.jsx';
import {
    LuPlus, LuPalette, LuEye, LuEyeOff,
    LuCircleHelp, LuStar, LuShieldCheck, LuImage,
    LuMail, LuClock, LuMessageSquare, LuLayoutGrid, LuX
} from 'react-icons/lu';
import ButtonStylingToolbar from '../components/ButtonStylingToolbar.jsx';
import { slugify, getMinProductPrice, extractProductAttributes, findMatchingVariation } from '../utils/helpers';
import {
    addUnit, getApiUrl, authHeaders, hexToRgba, parseAlpha, resolveMediaUrl,
    getTenantStorageKey, getFontStyles, getShadow, getSideBorder, smoothScrollTo,
    findAnchorElement, getBtnColor, getBtnTextColor, getBackground, resolveSectionBg,
    buildBtnStyleBase, buildImgStyleBase, safePost
} from '../utils/styleHelpers';
import { ECOMMERCE_BRANDING } from './branding.js';

// Sub-components
import { ShopBreadcrumbs } from './components/ShopBreadcrumbs.jsx';
import { ShopHero } from './components/ShopHero.jsx';
import { ShopFilterBar } from './components/ShopFilterBar.jsx';
import { ShopProductGrid } from './components/ShopProductGrid.jsx';
import { ShopCustomizerDrawer } from './components/ShopCustomizerDrawer.jsx';
import { ProductExpandedEditModal } from './components/ProductExpandedEditModal.jsx';
import { BUTTON_STYLE_PRESETS, IMAGE_PADDING_PRESETS, SHOP_THEME_PRESETS } from './shopPresets.js';

const SectionCanvas = lazy(() => import('../components/SectionCanvas.jsx'));

// Classic, clean shop section templates
const SHOP_SECTION_TEMPLATES = [
    {
        key: 'faq',
        name: 'FAQ Accordion',
        description: 'Answers to shipping, returns, order inquiries, and guarantees.',
        icon: LuCircleHelp,
        color: '#2563eb',
        bg: '#eff6ff',
        create: (genId) => ({
            id: genId('shop-faq'),
            title: 'Frequently Asked Questions',
            type: 'columns',
            backgroundColor: '#ffffff',
            backgroundImage: '',
            minHeight: 'auto',
            padding: '4rem 2rem',
            heading: 'Frequently Asked Questions',
            subHeading: 'Everything you need to know about our products, shipping, and guarantees.',
            columns: [
                {
                    id: genId('col-shop'),
                    width: 6,
                    heading: '📦 How does shipping and delivery work?',
                    text: '<p>We ship all orders within 1-2 business days. Standard delivery takes 3-5 business days with full tracking provided upon dispatch.</p>'
                },
                {
                    id: genId('col-shop'),
                    width: 6,
                    heading: '🔄 What is your return & exchange policy?',
                    text: '<p>We offer a 30-day hassle-free return policy. If you are not completely satisfied with your purchase, contact us for a prepaid return label.</p>'
                },
                {
                    id: genId('col-shop'),
                    width: 6,
                    heading: '🔒 Are payments safe and secure?',
                    text: '<p>Yes, all transactions are encrypted using industry-standard SSL technology. We support all major credit cards, PayPal, and Apple Pay.</p>'
                },
                {
                    id: genId('col-shop'),
                    width: 6,
                    heading: '✨ Do you offer custom or bulk orders?',
                    text: '<p>Absolutely! We specialize in tailored requests and wholesale orders. Reach out via our contact section or email for bespoke inquiries.</p>'
                }
            ]
        })
    },
    {
        key: 'testimonials',
        name: 'Customer Reviews',
        description: 'Highlight buyer satisfaction with 3 quote cards and star ratings.',
        icon: LuStar,
        color: '#d97706',
        bg: '#fffbeb',
        create: (genId) => ({
            id: genId('shop-test'),
            title: 'Customer Testimonials',
            type: 'columns',
            backgroundColor: '#f8fafc',
            backgroundImage: '',
            minHeight: 'auto',
            padding: '4.5rem 2rem',
            heading: 'Loved by Customers Worldwide',
            subHeading: 'Read genuine experiences from our verified collectors and patrons.',
            columns: [
                {
                    id: genId('col-shop'),
                    width: 4,
                    heading: '⭐⭐⭐⭐⭐ "Remarkable Quality"',
                    text: '<p><em>"The craftsmanship exceeded all expectations. Fast shipping, beautiful packaging, and the piece looks stunning in person."</em></p><p><strong>— Elena Rostova</strong>, Verified Buyer</p>'
                },
                {
                    id: genId('col-shop'),
                    width: 4,
                    heading: '⭐⭐⭐⭐⭐ "Exceptional Service"',
                    text: '<p><em>"Superb customer care and immediate responsiveness. The attention to detail is evident in every single aspect."</em></p><p><strong>— Marcus Vance</strong>, Verified Buyer</p>'
                },
                {
                    id: genId('col-shop'),
                    width: 4,
                    heading: '⭐⭐⭐⭐⭐ "A Rare Find"',
                    text: '<p><em>"Hands down the best purchase I made this year. High aesthetic value, durable build, and exactly as described."</em></p><p><strong>— Sophia Lind</strong>, Verified Buyer</p>'
                }
            ]
        })
    },
    {
        key: 'features',
        name: 'Trust & Value Highlights',
        description: 'Showcase free delivery, satisfaction guarantees, and artisan standards.',
        icon: LuShieldCheck,
        color: '#059669',
        bg: '#ecfdf5',
        create: (genId) => ({
            id: genId('shop-feat'),
            title: 'Why Choose Us',
            type: 'columns',
            backgroundColor: '#ffffff',
            backgroundImage: '',
            minHeight: 'auto',
            padding: '4rem 2rem',
            heading: 'The Omnivaso Standard',
            subHeading: 'Crafted with intention, backed by our ironclad guarantees.',
            columns: [
                {
                    id: genId('col-shop'),
                    width: 4,
                    heading: '🚀 Express Worldwide Shipping',
                    text: '<p>Carefully packaged and insured courier delivery directly to your doorstep in over 60 countries.</p>'
                },
                {
                    id: genId('col-shop'),
                    width: 4,
                    heading: '🛡️ 100% Satisfaction Guarantee',
                    text: '<p>Shop with confidence. If an item does not meet your standard, our team will make it right or refund you in full.</p>'
                },
                {
                    id: genId('col-shop'),
                    width: 4,
                    heading: '🌿 Ethically Sourced & Made',
                    text: '<p>Constructed using premium sustainable materials and ethical workshop practices designed to last a lifetime.</p>'
                }
            ]
        })
    },
    {
        key: 'gallery',
        name: 'Lookbook & Visual Showcase',
        description: 'High-resolution photo grid with lightbox zoom and custom columns.',
        icon: LuImage,
        color: '#7c3aed',
        bg: '#f5f3ff',
        create: (genId) => ({
            id: genId('shop-gal'),
            title: 'Visual Lookbook',
            type: 'gallery',
            backgroundColor: '#ffffff',
            backgroundImage: '',
            minHeight: 'auto',
            padding: '4rem 2rem',
            heading: 'Visual Lookbook',
            subHeading: 'Explore curated scenes, styling inspiration, and craftsmanship details.',
            galleryColumns: 3,
            galleryGap: '16px',
            galleryBorderRadius: '8px',
            galleryShadow: 'md',
            galleryHoverEffect: 'scale',
            galleryCaptionStyle: 'overlay',
            columns: [
                { id: genId('col-shop'), image: 'https://images.unsplash.com/photo-1513519245088-0e12902e5a38?w=800', heading: 'Studio Atmosphere', text: 'Sculptural elegance.' },
                { id: genId('col-shop'), image: 'https://images.unsplash.com/photo-1544816155-12df9643f363?w=800', heading: 'Detail Study', text: 'Handcrafted textures.' },
                { id: genId('col-shop'), image: 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=800', heading: 'Modern Living', text: 'Curated space.' }
            ]
        })
    },
    {
        key: 'newsletter',
        name: 'Newsletter & VIP Offer',
        description: 'Incentivize email signups with a first-order discount banner.',
        icon: LuMail,
        color: '#0284c7',
        bg: '#f0f9ff',
        create: (genId) => ({
            id: genId('shop-news'),
            title: 'Newsletter Subscription',
            type: 'newsletter',
            backgroundColor: '#0f172a',
            textColor: '#ffffff',
            backgroundImage: '',
            minHeight: 'auto',
            padding: '3.5rem 2rem',
            heading: 'Enjoy 10% Off Your First Order',
            text: 'Join the Omnivaso Collectors Circle to receive priority access to new releases, archival sales, and exclusive previews.',
            buttonText: 'Claim Your 10% Off',
            newsletterVariant: 'banner'
        })
    },
    {
        key: 'hours',
        name: 'Store Hours & Location',
        description: 'Display physical showroom/store hours, address, and visit details.',
        icon: LuClock,
        color: '#4f46e5',
        bg: '#eef2ff',
        create: (genId) => ({
            id: genId('shop-hrs'),
            title: 'Store Hours & Studio Visit',
            type: 'business_hours',
            backgroundColor: '#f8fafc',
            backgroundImage: '',
            minHeight: 'auto',
            padding: '4rem 2rem',
            heading: 'Visit Our Showroom',
            subHeading: 'Experience the collection in person. Open for walk-ins and private appointments.',
            primaryColor: '#2563eb',
            cardBgColor: '#ffffff',
            textColor: '#0f172a',
            mutedColor: '#64748b'
        })
    },
    {
        key: 'contact',
        name: 'Contact & Inquiries',
        description: 'Direct inquiry form for custom commissions and customer support.',
        icon: LuMessageSquare,
        color: '#0d9488',
        bg: '#f0fdfa',
        create: (genId) => ({
            id: genId('shop-contact'),
            title: 'Customer Inquiries & Support',
            type: 'contact',
            backgroundColor: '#ffffff',
            backgroundImage: '',
            minHeight: 'auto',
            padding: '4rem 2rem',
            heading: 'Have a Question About an Item?',
            text: 'Our concierge team is here to assist with product inquiries, customization, or order updates.',
            contactVariant: 'card',
            showSubject: true,
            showPhone: true
        })
    },
    {
        key: 'custom',
        name: 'Custom Columns',
        description: 'Multi-column layout ready for custom text, images, and buttons.',
        icon: LuLayoutGrid,
        color: '#475569',
        bg: '#f8fafc',
        create: (genId) => ({
            id: genId('shop-custom'),
            title: 'Custom Content Section',
            type: 'columns',
            backgroundColor: '#ffffff',
            backgroundImage: '',
            minHeight: 'auto',
            padding: '4rem 2rem',
            heading: 'Section Title',
            subHeading: 'Add your introductory text or subtitle here.',
            columns: [
                {
                    id: genId('col-shop'),
                    width: 6,
                    heading: 'First Feature',
                    text: '<p>Customize this content with images, rich text, buttons, and layouts.</p>'
                },
                {
                    id: genId('col-shop'),
                    width: 6,
                    heading: 'Second Feature',
                    text: '<p>Use the column toolbar to add more columns or adjust sizing.</p>'
                }
            ]
        })
    }
];

export { BUTTON_STYLE_PRESETS, IMAGE_PADDING_PRESETS, SHOP_THEME_PRESETS };

const SS_DEFAULTS = {
    primaryColor: '#3b82f6', secondaryColor: '#1e293b', pageBackground: '#f8fafc',
    shopCardBg: '#ffffff', shopCardBorder: '1px solid #e2e8f0', shopCardBorderRadius: '12px',
    shopImageBorder: 'none', shopImageBorderRadius: '0px', shopGridColumns: '3',
    shopImageGraphicBorder: 'none',
    shopImageGraphicBorderUrl: '',
    shopImageGraphicBorderWidth: '18px',
    shopImageGraphicBorderSlice: '30',
    shopImageGraphicBorderRepeat: 'round',
    shopImageGraphicBorderOutset: '0px',
    shopImageGraphicBorderColor: '',
    shopImageGraphicBorderPadding: '12px',
    shopGridTabletColumns: '2', shopGridMobileColumns: '1',
    shopGridGap: '30px', shopGridMobileGap: '16px',
    productCardBorder: '1px solid #e2e8f0', productCardBorderRadius: '16px',
    shopCardPadding: '20px',
    shopCardContentPadding: '20px',
    shopCardContentPaddingSides: '',
    shopCardContentPaddingTop: '',
    shopCardContentPaddingBottom: '',
    shopCardContentPaddingLeft: '',
    shopCardContentPaddingRight: '',
    productImageBorder: '1px solid #f1f5f9', productImageBorderRadius: '12px',
    productCardShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    productPageBg: '#f8fafc', productCardBg: '#ffffff',
    btnColor: '#3b82f6', btnTextColor: '#ffffff', btnRadius: '6px', btnPadding: '8px 16px',
    btnBorder: 'none', btnShadow: 'none',
    btnHoverBg: '', btnHoverColor: '', btnHoverBorder: '', btnHoverShadow: '',
    detailBtnBg: '#f8fafc', detailBtnColor: '#475569', detailBtnBorder: '1px solid #cbd5e1',
    detailBtnShadow: 'none', detailBtnHoverBg: '', detailBtnHoverColor: '', detailBtnHoverBorder: '',
    shopFilterPanelBg: '#ffffff',
    shopFilterPanelBorder: '1px solid #e2e8f0',
    shopFilterPanelRadius: '12px',
    shopFilterPanelShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    shopFilterPanelDirection: 'column',
    shopCardDirection: 'column',
    shopCardShowOptions: true,
    shopImageObjectFit: 'cover',
    shopCardImageFit: 'cover',
    shopCardImageAspectRatio: 'auto',
    shopImageBg: 'transparent',
    shopCardImageBg: 'transparent',
    shopImagePadding: '0px',
    shopImagePaddingTop: '',
    shopImagePaddingBottom: '',
    shopImagePaddingSides: '',
    shopImagePaddingLeft: '',
    shopImagePaddingRight: '',
    shopCardImagePadding: '0px',
    shopCardImagePaddingTop: '',
    shopCardImagePaddingBottom: '',
    shopCardImagePaddingSides: '',
    shopCardImagePaddingLeft: '',
    shopCardImagePaddingRight: '',
    shopImageInnerRadius: '0px',
    shopQuantityBorder: '1px solid #cbd5e1',
    shopQuantityRadius: '6px',
    shopQuantityBg: '#ffffff',
    shopQuantityTextColor: '#0f172a',
    shopQuantityBtnBg: '#f1f5f9',
    shopQuantityBtnColor: '#475569',
    shopCardShowQuantity: false,
    shopCardImageWidth: '40%',
    shopCardImageHeight: '220px',
    shopCardContentAlign: 'left',
    shopCardContentGap: '10px',
    shopCardOrderCategory: 1,
    shopCardOrderName: 2,
    shopCardOrderDesc: 3,
    shopCardOrderActions: 4,
    shopCardActionsDirection: 'row',
    shopCardActionsAlign: 'space-between',
    shopCardButtonsDirection: 'row',
    shopCardButtonsLayout: 'row',
    shopCardButtonsAlign: 'flex-end',
    shopCardButtonsGap: '8px',
    shopCardButtonsWidth: 'auto',
    shopCardBtnFontFamily: 'inherit',
    shopCardBtnFontSize: '0.85rem',
    shopCardBtnFontWeight: '600',
    shopCardBtnTransform: 'none',
    shopCardBtnLetterSpacing: 'normal',
    shopCatContainerBg: 'transparent',
    shopCatContainerBorder: 'none',
    shopCatContainerRadius: '0px',
    shopCatContainerPadding: '0px',
    shopCatContainerPaddingTop: '',
    shopCatContainerPaddingBottom: '',
    shopCatContainerPaddingSides: '',
    shopCatContainerPaddingLeft: '',
    shopCatContainerPaddingRight: '',
    shopCatContainerShadow: 'none',
    shopCategoriesPlacement: 'below-heading',
    shopCatLayoutDirection: 'row',
    shopCatButtonsLayoutDirection: 'row',
    shopCatAlign: 'flex-start',
    shopCatGap: '10px',
    shopCatButtonsGap: '8px',
    shopCatLabelHide: false,
    shopCatLabelFontFamily: 'inherit',
    shopCatLabelFontSize: '0.9rem',
    shopCatLabelFontWeight: 'bold',
    shopCatLabelColor: '#334155',
    shopCatLabelTransform: 'none',
    shopCatBtnFontFamily: 'inherit',
    shopCatBtnFontSize: '0.85rem',
    shopCatBtnFontWeight: '500',
    shopCatBtnTransform: 'none',
    shopCatBtnBg: '#f1f5f9',
    shopCatBtnColor: '#475569',
    shopCatBtnOpacity: 100,
    shopCatBtnActiveBg: '#3b82f6',
    shopCatBtnActiveColor: '#ffffff',
    shopCatBtnActiveOpacity: 100,
    shopCatBtnBorder: 'none',
    shopCatBtnRadius: '20px',
    shopCatBtnPadding: '8px 16px',
    shopCatBtnShadow: 'none',
    shopSubCatContainerBg: 'transparent',
    shopSubCatContainerBorder: 'none',
    shopSubCatContainerRadius: '0px',
    shopSubCatContainerPadding: '0px',
    shopSubCatDivider: '2px solid #cbd5e1',
    shopSubCatGap: '6px',
    shopSearchPlacement: 'inside-right',
    shopSearchWidth: '300px',
    shopSearchAlign: 'stretch',
    shopSearchBg: '#ffffff',
    shopSearchColor: '#0f172a',
    shopSearchBorder: '1px solid #cbd5e1',
    shopSearchRadius: '8px',
    shopSearchPadding: '10px 16px',
    shopSearchFontSize: '1rem',
    shopSearchFontFamily: 'inherit',
    shopSortShow: true,
    shopSortLabel: 'Sort:',
    shopSortLabelHide: false,
    shopSortLabelColor: '#475569',
    shopSortLabelFontSize: '0.85rem',
    shopSortLabelFontWeight: '600',
    shopSortBg: '#ffffff',
    shopSortColor: '#0f172a',
    shopSortBorder: '1px solid #cbd5e1',
    shopSortRadius: '8px',
    shopSortPadding: '10px 16px',
    shopSortFontSize: '0.85rem',
    shopSortFontFamily: 'inherit',
    shopSortShadow: 'none',
    shopProductCategoryColor: '#3b82f6',
    shopProductCategorySize: '0.8rem',
    shopProductCategoryFontFamily: 'inherit',
    shopProductCategoryWeight: 'bold',
    shopProductCategoryTransform: 'uppercase',
    shopProductNameColor: '#0f172a',
    shopProductNameSize: '1.2rem',
    shopProductNameFontFamily: 'inherit',
    shopProductDescShow: true,
    shopProductDescColor: '#64748b',
    shopProductDescSize: '0.9rem',
    shopProductDescFontFamily: 'inherit',
    shopProductDescWeight: 'normal',
    shopProductDescLineHeight: '1.5',
    shopProductDescLineClamp: '2',
    shopProductDescAlign: '',
    shopProductDescTransform: 'none',
    shopProductDescMargin: '0px',
    shopProductPriceColor: '#0f172a',
    shopProductPriceSize: '1.25rem',
    shopProductPriceFontFamily: 'inherit',
    shopCategoryPathShow: true,
    shopCategoryPathFontFamily: 'inherit',
    shopCategoryPathSize: '0.85rem',
    shopCategoryPathWeight: '600',
    shopCategoryPathColor: '#64748b',
    shopCategoryPathActiveColor: '#0f172a',
    shopCategoryPathLinkColor: '#3b82f6',
    shopCategoryPathTransform: 'none',
    shopCategoryPathSeparator: '›',
    shopCategoryPathBg: 'transparent',
    shopCategoryPathPadding: '0px',
    shopCategoryPathMarginBottom: '20px',
    shopCategoryPathBorder: 'none',
    shopCategoryPathRadius: '0px',
    shopAllHeadingShow: true,
    shopCategoryHeadingShow: true,
    shopHeroHeading: '',
    shopHeroSubheading: '',
    shopHeroHeadingMobileSize: '',
    shopHeroHeadingMobileAlign: '',
    shopProductNameMobileSize: '',
    shopProductNameMobileAlign: '',
    shopProductPriceMobileSize: '',
    shopProductPriceMobileAlign: '',
    shopProductDescMobileSize: '',
    shopProductDescMobileAlign: '',
    shopProductCategoryMobileSize: '',
    shopProductCategoryMobileAlign: '',
    shopCardMobilePadding: '',
    shopCardMobileDirection: '',
    shopCardMobileHeight: '',
    shopCardMobileMinHeight: '',
    shopCardMobileMaxHeight: '',
    shopCardImageMobileHeight: '',
    shopCardImageMobileAspectRatio: '',
    shopCardImageMobileFit: '',
    shopCardImageMobileWidth: '',
    shopImageMobilePadding: '',
    shopImageMobileBorderRadius: '',
    shopCardBtnMobileHeight: '',
    shopCardBtnMobilePadding: '',
    shopCardBtnMobileFontSize: '',
    shopCardContentMobileGap: '',
    shopCardActionRowMobileGap: '',
    shopCardMobileActionRowDirection: '',
    shopCardMobileButtonsLayout: '',
    shopCardButtonsMobileGap: '',
    shopCatBarMobileScroll: 'wrap',
    shopCatBtnMobileSize: '',
    shopCatBtnMobilePadding: '',
    shopPageMobilePadding: '',
    shopPageMobilePaddingTop: '',
    shopPageMobileMargin: '',
    shopPagePaddingRight: '',
    shopPagePaddingBottom: '',
    shopPagePaddingLeft: '',
    shopCustomCss: '',
    pageBackgroundImage: '',
    shopPageBgImage: '',
    productPageBgImage: '',
    pageBackgroundRepeat: 'no-repeat',
    pageBackgroundSize: 'cover',
    pageBackgroundPosition: 'center',
    pageBackgroundAttachment: 'scroll',
    pageBgOverlayColor: '#000000',
    pageBgOverlayOpacity: 0
};

const initSS = (s = {}) => ({ ...SS_DEFAULTS, ...(s || {}) });

export const ShopPage = ({ config, onSaveConfig, isAuthenticated: authProp }) => {
    const location = useLocation();
    const params = useParams();
    const isDemo = typeof window !== 'undefined' && (
        localStorage.getItem('omnivaso_demo_mode') === 'true' ||
        window.location.search.includes('demo=true') ||
        (location?.search && location.search.includes('demo='))
    );
    const isAuthenticated = authProp !== undefined ? authProp : (typeof window !== 'undefined' && (!!localStorage.getItem('adminToken') || isDemo));

    // --- LOCAL DATA COPY FOR INLINE EDITING ---
    const [data, setData] = useState(config || {});
    const [isEditing, setIsEditing] = useState(false);
    const [isEditingBarMinimized, setIsEditingBarMinimized] = useState(false);
    const [editSnapshot, setEditSnapshot] = useState(null);
    const [expandedProductModal, setExpandedProductModal] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showUnpublishedInEdit, setShowUnpublishedInEdit] = useState(() => {
        try {
            const stored = localStorage.getItem('omnivaso_shop_show_unpublished_edit');
            if (stored !== null) return stored === 'true';
        } catch (_) {}
        return true;
    });

    useEffect(() => {
        try {
            localStorage.setItem('omnivaso_shop_show_unpublished_edit', String(showUnpublishedInEdit));
        } catch (_) {}
    }, [showUnpublishedInEdit]);

    useEffect(() => {
        if (config && !isEditing) setData(config);
    }, [config, isEditing]);

    const { settings = {}, typography = {}, footer = [], socials = [], modules = {} } = data || {};
    const [showCustomizer, setShowCustomizer] = useState(false);
    const [customizerTrigger, setCustomizerTrigger] = useState(0);
    const [showButtonStyling, setShowButtonStyling] = useState(false);
    const [customizerTab, setCustomizerTab] = useState('theme');
    const [isSimpleMode, setIsSimpleMode] = useState(false);
    const [selectedSimpleSection, setSelectedSimpleSection] = useState(null);

    const [styleSettings, setStyleSettings] = useState(() => initSS(settings));
    const [savedStyleSnapshot, setSavedStyleSnapshot] = useState(null);
    const isCategoriesDirtyRef = useRef(false);
    const isProductsDirtyRef = useRef(false);
    const isCommercialMode = Boolean(styleSettings?.shopCommercialOrderForm || settings?.shopCommercialOrderForm);

    useEffect(() => { 
        if (config && config.settings && !showCustomizer && !isEditing) {
            setStyleSettings(initSS(config.settings)); 
        }
    }, [config, showCustomizer, isEditing]);
    const { addToCart, addBatchToCart, cartCount } = useCart();
    const { formatPrice } = useCurrency();
    const navigate = useNavigate();

    const isCategoryMatch = (productCat, targetCat) => {
        if (!targetCat || targetCat === 'All') return true;
        if (!productCat) return false;
        if (productCat === targetCat) return true;
        const normProduct = String(productCat).toLowerCase().trim();
        const normTarget = String(targetCat).toLowerCase().trim();
        if (normProduct === normTarget) return true;
        if (slugify(normProduct) === slugify(normTarget)) return true;
        return false;
    };



    const normalizeProduct = (p) => {
        if (!p) return p;
        let cats = [];
        if (Array.isArray(p.categories)) {
            p.categories.forEach(c => {
                if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
            });
        } else if (typeof p.categories === 'string' && p.categories.trim()) {
            try {
                const parsed = JSON.parse(p.categories);
                if (Array.isArray(parsed)) {
                    parsed.forEach(c => {
                        if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                        else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
                    });
                }
            } catch (e) {
                if (p.categories.includes(',')) {
                    p.categories.split(',').forEach(c => { if (c.trim()) cats.push(c.trim()); });
                } else {
                    cats.push(p.categories.trim());
                }
            }
        }
        if (p.pageConfig && Array.isArray(p.pageConfig.categories)) {
            p.pageConfig.categories.forEach(c => {
                if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
            });
        }
        if (typeof p.category === 'string' && p.category.trim()) {
            if (p.category.includes(',')) {
                p.category.split(',').forEach(c => { if (c.trim()) cats.push(c.trim()); });
            } else {
                cats.push(p.category.trim());
            }
        } else if (p.category && typeof p.category === 'object' && p.category.name) {
            cats.push(String(p.category.name).trim());
        }
        const cleanCats = Array.from(new Set(cats.filter(Boolean)));
        let vars = p.variations;
        if (typeof vars === 'string' && vars.trim()) {
            try { vars = JSON.parse(vars); } catch (e) { vars = []; }
        }
        let attrs = p.attributes;
        if (typeof attrs === 'string' && attrs.trim()) {
            try { attrs = JSON.parse(attrs); } catch (e) { attrs = {}; }
        }
        return {
            ...p,
            variations: Array.isArray(vars) ? vars : p.variations,
            attributes: (attrs && typeof attrs === 'object') ? attrs : p.attributes,
            categories: cleanCats,
            category: cleanCats[0] || p.category || ''
        };
    };

    const getProductCategoriesList = (p) => {
        if (!p) return [];
        const cats = [];
        if (Array.isArray(p.categories)) {
            p.categories.forEach(c => {
                if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
            });
        } else if (typeof p.categories === 'string' && p.categories.trim()) {
            try {
                const parsed = JSON.parse(p.categories);
                if (Array.isArray(parsed)) {
                    parsed.forEach(c => {
                        if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                        else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
                    });
                }
            } catch (e) {
                if (p.categories.includes(',')) {
                    p.categories.split(',').forEach(c => { if (c.trim()) cats.push(c.trim()); });
                } else {
                    cats.push(p.categories.trim());
                }
            }
        }
        if (p.pageConfig && Array.isArray(p.pageConfig.categories)) {
            p.pageConfig.categories.forEach(c => {
                if (typeof c === 'string' && c.trim()) cats.push(c.trim());
                else if (c && typeof c === 'object' && c.name && String(c.name).trim()) cats.push(String(c.name).trim());
            });
        }
        if (typeof p.category === 'string') {
            if (p.category.includes(',')) {
                p.category.split(',').forEach(c => cats.push(c.trim()));
            } else {
                cats.push(p.category.trim());
            }
        } else if (p.category && typeof p.category === 'object' && p.category.name) {
            cats.push(String(p.category.name).trim());
        }
        if (typeof p.subCategory === 'string') {
            if (p.subCategory.includes(',')) {
                p.subCategory.split(',').forEach(c => cats.push(c.trim()));
            } else {
                cats.push(p.subCategory.trim());
            }
        } else if (p.subCategory && typeof p.subCategory === 'object' && p.subCategory.name) {
            cats.push(String(p.subCategory.name).trim());
        }
        return Array.from(new Set(cats.filter(Boolean)));
    };

    const isSubCategoryMatch = (productSubCat, targetSubCat) => {
        if (!targetSubCat || targetSubCat === 'All') return true;
        if (!productSubCat) return false;
        if (productSubCat === targetSubCat) return true;
        const normProduct = String(productSubCat).toLowerCase().trim();
        const normTarget = String(targetSubCat).toLowerCase().trim();
        if (normProduct === normTarget) return true;
        if (slugify(normProduct) === slugify(normTarget)) return true;
        return false;
    };



    const [products, setProducts] = useState(() => {
        try {
            const cached = localStorage.getItem(getTenantStorageKey('cached_products'));
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed.map(normalizeProduct);
            }
        } catch (e) {}
        return [];
    });
    const [loading, setLoading] = useState(() => products.length === 0);
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('All');
    const [sortBy, setSortBy] = useState('category');
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

    const isMobile = windowWidth <= 768;
    const isTablet = windowWidth > 768 && windowWidth <= 1024;
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [headerHeight, setHeaderHeight] = useState(null);

    // Measure the actual rendered nav height so the header-spacer is always accurate
    useEffect(() => {
        let rafId;
        const measure = () => {
            rafId = requestAnimationFrame(() => {
                const nav = document.querySelector('.site-navigation');
                if (nav) {
                    const h = nav.offsetHeight || nav.getBoundingClientRect().height;
                    setHeaderHeight(prev => (prev === h ? prev : h));
                }
            });
        };
        measure();
        const nav = document.querySelector('.site-navigation');
        if (!nav) return;
        const ro = new ResizeObserver(measure);
        ro.observe(nav);
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            ro.disconnect();
        };
    }, []);

    // Scroll to product card or anchor if hash / state is present in URL
    useEffect(() => {
        const targetId = location.hash ? location.hash.replace(/^#/, '') : location.state?.targetId;
        if (!targetId) return;

        const timer = setTimeout(() => {
            if (!smoothScrollTo(targetId, 600, headerHeight || 80)) {
                if (targetId === 'top' || targetId === 'header') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    const fallbackEl = document.getElementById(targetId) ||
                                       document.getElementById(`product-${targetId}`) ||
                                       document.querySelector(`[data-product-id="${targetId}"]`) ||
                                       document.querySelector(`[data-product-slug="${targetId}"]`);
                    if (fallbackEl) {
                        fallbackEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [location.hash, location.state, products, loading, headerHeight]);

    // Global click listener for smooth scroll links on Shop Page
    useEffect(() => {
        const handleHashClick = (e) => {
            const link = e.target.closest('a[href^="#"], [data-anchor-target]');
            if (!link) return;
            const href = link.getAttribute('href') || link.getAttribute('data-anchor-target');
            if (href && href.startsWith('#')) {
                const rawId = href.replace(/^#/, '');
                if (!rawId) {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    return;
                }
                if (findAnchorElement(rawId) || document.getElementById(rawId)) {
                    e.preventDefault();
                    smoothScrollTo(rawId, 600, headerHeight || 80);
                }
            }
        };
        document.addEventListener('click', handleHashClick);
        return () => document.removeEventListener('click', handleHashClick);
    }, [headerHeight]);

    // Default fallback hero for Shop pages
    const defaultShopHero = useMemo(() => {
        const headingWeight = settings.shopHeroHeadingWeight || settings.shopHeroHeadingFontWeight || styleSettings.shopHeroHeadingWeight || '800';
        const subheadingWeight = settings.shopHeroSubheadingWeight || settings.shopHeroSubheadingFontWeight || styleSettings.shopHeroSubheadingWeight || '400';
        const headingSize = settings.shopHeroHeadingSize || '';
        const subheadingSize = settings.shopHeroSubheadingSize || '';
        const headingColor = settings.shopHeroHeadingColor || '#ffffff';
        const subheadingColor = settings.shopHeroSubheadingColor || 'rgba(255, 255, 255, 0.85)';
        const headingAlign = settings.shopHeroHeadingAlign || 'center';
        const subheadingAlign = settings.shopHeroSubheadingAlign || 'center';
        const headingFontFamily = settings.shopHeroHeadingFontFamily || '';
        const subheadingFontFamily = settings.shopHeroSubheadingFontFamily || '';

        return {
            title: (settings.shopHeroHeading !== undefined && settings.shopHeroHeading !== null) 
                ? settings.shopHeroHeading 
                : (data?.shopHero?.title !== undefined ? data.shopHero.title : (ECOMMERCE_BRANDING.shopHeroHeading || 'Shop & Catalog')),
            subtitle: (settings.shopHeroSubheading !== undefined && settings.shopHeroSubheading !== null) 
                ? settings.shopHeroSubheading 
                : (data?.shopHero?.subtitle !== undefined ? data.shopHero.subtitle : (ECOMMERCE_BRANDING.shopHeroSubheading || 'Explore our full collection of products.')),
            titleFontWeight: headingWeight,
            titleWeight: headingWeight,
            fontWeight: headingWeight,
            weight: headingWeight,
            titleSize: headingSize,
            titleFontSize: headingSize,
            titleColor: headingColor,
            titleAlign: headingAlign,
            titleFontFamily: headingFontFamily,
            titleTextShadowPreset: data?.shopHero?.titleTextShadowPreset || 'none',
            titleTextShadow: data?.shopHero?.titleTextShadow || '',
            titleTextShadowCustom: data?.shopHero?.titleTextShadowCustom || '',
            subtitleFontWeight: subheadingWeight,
            subtitleWeight: subheadingWeight,
            subFontWeight: subheadingWeight,
            subWeight: subheadingWeight,
            subtitleSize: subheadingSize,
            subtitleFontSize: subheadingSize,
            subtitleColor: subheadingColor,
            subtitleAlign: subheadingAlign,
            subtitleFontFamily: subheadingFontFamily,
            subtitleTextShadowPreset: data?.shopHero?.subtitleTextShadowPreset || 'none',
            subtitleTextShadow: data?.shopHero?.subtitleTextShadow || '',
            subtitleTextShadowCustom: data?.shopHero?.subtitleTextShadowCustom || '',
            buttons: [
                { text: '🛍️ Browse Products', link: '#shop-catalog-anchor', variant: 'primary' }
            ],
            backgroundColor: settings.pageBackground || '#090d16',
            textColor: '#ffffff',
            buttonsAlign: 'center',
            minHeight: '280px',
            hideHero: false,
            overlayOpacity: 0.45,
            overlayColor: '#000000',
            overlayType: 'solid'
        };
    }, [
        settings.shopHeroHeading,
        settings.shopHeroSubheading,
        settings.shopHeroHeadingWeight,
        settings.shopHeroHeadingFontWeight,
        settings.shopHeroSubheadingWeight,
        settings.shopHeroSubheadingFontWeight,
        settings.shopHeroHeadingSize,
        settings.shopHeroSubheadingSize,
        settings.shopHeroHeadingColor,
        settings.shopHeroSubheadingColor,
        settings.shopHeroHeadingAlign,
        settings.shopHeroSubheadingAlign,
        settings.shopHeroHeadingFontFamily,
        settings.shopHeroSubheadingFontFamily,
        settings.pageBackground,
        styleSettings.shopHeroHeadingWeight,
        styleSettings.shopHeroSubheadingWeight,
        data?.shopHero?.title,
        data?.shopHero?.subtitle,
        data?.shopHero?.titleTextShadowPreset,
        data?.shopHero?.titleTextShadow,
        data?.shopHero?.subtitleTextShadowPreset,
        data?.shopHero?.subtitleTextShadow
    ]);

    // Active Shop Hero configuration
    const shopHero = useMemo(() => {
        const raw = data?.shopHero || data?.shop?.hero;
        if (raw && typeof raw === 'object') {
            const rawTitleWeight = raw.titleFontWeight || raw.titleWeight || raw.fontWeight || raw.weight;
            const rawSubWeight = raw.subtitleFontWeight || raw.subtitleWeight || raw.subFontWeight || raw.subWeight;
            const rawTitleSize = raw.titleSize || raw.titleFontSize;
            const rawSubSize = raw.subtitleSize || raw.subtitleFontSize;

            return {
                ...defaultShopHero,
                ...raw,
                title: raw.title !== undefined ? raw.title : defaultShopHero.title,
                subtitle: raw.subtitle !== undefined ? raw.subtitle : defaultShopHero.subtitle,
                titleFontWeight: rawTitleWeight !== undefined ? rawTitleWeight : defaultShopHero.titleFontWeight,
                titleWeight: rawTitleWeight !== undefined ? rawTitleWeight : defaultShopHero.titleWeight,
                fontWeight: rawTitleWeight !== undefined ? rawTitleWeight : defaultShopHero.fontWeight,
                weight: rawTitleWeight !== undefined ? rawTitleWeight : defaultShopHero.weight,
                subtitleFontWeight: rawSubWeight !== undefined ? rawSubWeight : defaultShopHero.subtitleFontWeight,
                subtitleWeight: rawSubWeight !== undefined ? rawSubWeight : defaultShopHero.subtitleWeight,
                subFontWeight: rawSubWeight !== undefined ? rawSubWeight : defaultShopHero.subFontWeight,
                subWeight: rawSubWeight !== undefined ? rawSubWeight : defaultShopHero.subWeight,
                titleSize: rawTitleSize !== undefined ? rawTitleSize : defaultShopHero.titleSize,
                titleFontSize: rawTitleSize !== undefined ? rawTitleSize : defaultShopHero.titleFontSize,
                subtitleSize: rawSubSize !== undefined ? rawSubSize : defaultShopHero.subtitleSize,
                subtitleFontSize: rawSubSize !== undefined ? rawSubSize : defaultShopHero.subtitleFontSize,
                buttons: Array.isArray(raw.buttons) ? raw.buttons : defaultShopHero.buttons
            };
        }
        return defaultShopHero;
    }, [data?.shopHero, data?.shop?.hero, defaultShopHero]);

    // Deep path updater with safe auto-vivification and functional state queuing
    const update = (path, value) => {
        setData(prevData => {
            const newData = JSON.parse(JSON.stringify(prevData || {}));
            if (path[0] === 'shopHero' && (!newData.shopHero || typeof newData.shopHero !== 'object')) {
                newData.shopHero = JSON.parse(JSON.stringify(shopHero));
            }
            let current = newData;
            for (let i = 0; i < path.length - 1; i++) {
                const key = path[i];
                const nextKey = path[i + 1];
                if (current[key] === undefined || current[key] === null) {
                    current[key] = (typeof nextKey === 'number' || !isNaN(Number(nextKey))) ? [] : {};
                }
                current = current[key];
            }
            current[path[path.length - 1]] = value;

            // Keep settings and shopHero bidirectionally synchronized
            if (path[0] === 'shopHero') {
                if (!newData.settings) newData.settings = {};
                const heroField = path[1];
                if (heroField === 'title') {
                    newData.settings.shopHeroHeading = value;
                } else if (heroField === 'subtitle') {
                    newData.settings.shopHeroSubheading = value;
                } else if (heroField === 'titleFontWeight' || heroField === 'titleWeight' || heroField === 'fontWeight' || heroField === 'weight') {
                    newData.settings.shopHeroHeadingWeight = value;
                    newData.settings.shopHeroHeadingFontWeight = value;
                    setStyleSettings(prev => ({ ...prev, shopHeroHeadingWeight: value, shopHeroHeadingFontWeight: value }));
                } else if (heroField === 'subtitleFontWeight' || heroField === 'subtitleWeight' || heroField === 'subFontWeight' || heroField === 'subWeight') {
                    newData.settings.shopHeroSubheadingWeight = value;
                    newData.settings.shopHeroSubheadingFontWeight = value;
                    setStyleSettings(prev => ({ ...prev, shopHeroSubheadingWeight: value, shopHeroSubheadingFontWeight: value }));
                } else if (heroField === 'titleSize' || heroField === 'titleFontSize') {
                    newData.settings.shopHeroHeadingSize = value;
                    setStyleSettings(prev => ({ ...prev, shopHeroHeadingSize: value }));
                } else if (heroField === 'subtitleSize' || heroField === 'subtitleFontSize') {
                    newData.settings.shopHeroSubheadingSize = value;
                    setStyleSettings(prev => ({ ...prev, shopHeroSubheadingSize: value }));
                } else if (heroField === 'titleColor') {
                    newData.settings.shopHeroHeadingColor = value;
                } else if (heroField === 'subtitleColor') {
                    newData.settings.shopHeroSubheadingColor = value;
                } else if (heroField === 'titleAlign') {
                    newData.settings.shopHeroHeadingAlign = value;
                } else if (heroField === 'subtitleAlign') {
                    newData.settings.shopHeroSubheadingAlign = value;
                }
            }
            if (path[0] === 'settings') {
                const settingKey = path[1];
                if (!newData.shopHero) newData.shopHero = JSON.parse(JSON.stringify(shopHero));
                if (settingKey === 'shopHeroHeading') {
                    newData.shopHero.title = value;
                } else if (settingKey === 'shopHeroSubheading') {
                    newData.shopHero.subtitle = value;
                } else if (settingKey === 'shopHeroHeadingWeight' || settingKey === 'shopHeroHeadingFontWeight') {
                    newData.shopHero.titleFontWeight = value;
                    newData.shopHero.titleWeight = value;
                    newData.shopHero.fontWeight = value;
                    newData.shopHero.weight = value;
                    setStyleSettings(prev => ({ ...prev, shopHeroHeadingWeight: value, shopHeroHeadingFontWeight: value }));
                } else if (settingKey === 'shopHeroSubheadingWeight' || settingKey === 'shopHeroSubheadingFontWeight') {
                    newData.shopHero.subtitleFontWeight = value;
                    newData.shopHero.subtitleWeight = value;
                    newData.shopHero.subFontWeight = value;
                    newData.shopHero.subWeight = value;
                    setStyleSettings(prev => ({ ...prev, shopHeroSubheadingWeight: value, shopHeroSubheadingFontWeight: value }));
                } else if (settingKey === 'shopHeroHeadingSize') {
                    newData.shopHero.titleSize = value;
                    newData.shopHero.titleFontSize = value;
                    setStyleSettings(prev => ({ ...prev, shopHeroHeadingSize: value }));
                } else if (settingKey === 'shopHeroSubheadingSize') {
                    newData.shopHero.subtitleSize = value;
                    newData.shopHero.subtitleFontSize = value;
                    setStyleSettings(prev => ({ ...prev, shopHeroSubheadingSize: value }));
                } else if (settingKey === 'shopHeroHeadingColor') {
                    newData.shopHero.titleColor = value;
                } else if (settingKey === 'shopHeroSubheadingColor') {
                    newData.shopHero.subtitleColor = value;
                } else if (settingKey === 'shopHeroHeadingAlign') {
                    newData.shopHero.titleAlign = value;
                } else if (settingKey === 'shopHeroSubheadingAlign') {
                    newData.shopHero.subtitleAlign = value;
                }
            }

            // Sync button & color changes to styleSettings for instant real-time shop preview
            if (path[0] === 'settings') {
                const key = path[1];
                if (key === 'pageBackgroundImage' || key === 'shopPageBgImage' || key === 'productPageBgImage') {
                    setStyleSettings(prev => ({
                        ...prev,
                        pageBackgroundImage: value,
                        shopPageBgImage: value,
                        productPageBgImage: value
                    }));
                } else if (key === 'pageBackground' || key === 'shopPageBgColor' || key === 'productPageBgColor') {
                    setStyleSettings(prev => ({
                        ...prev,
                        pageBackground: value,
                        shopPageBgColor: value,
                        productPageBgColor: value
                    }));
                } else if (key === 'pageBgMode' || key === 'pageBackgroundSize' || key === 'pageBackgroundPosition' || key === 'pageBackgroundRepeat' || key === 'pageBackgroundAttachment' || key === 'pageBgOverlayColor' || key === 'pageBgOverlayOpacity') {
                    setStyleSettings(prev => ({
                        ...prev,
                        [key]: value
                    }));
                } else if (key === 'primaryColor') {
                    setStyleSettings(prev => ({ ...prev, primaryColor: value, btnColor: value }));
                } else if (key === 'secondaryColor') {
                    setStyleSettings(prev => ({ ...prev, secondaryColor: value }));
                } else if (key === 'buttonTextColor') {
                    setStyleSettings(prev => ({ ...prev, btnTextColor: value }));
                } else if (key === 'buttonHoverBg') {
                    setStyleSettings(prev => ({ ...prev, btnHoverBg: value }));
                } else if (key === 'buttonHoverTextColor') {
                    setStyleSettings(prev => ({ ...prev, btnHoverColor: value }));
                } else if (key === 'buttonRadius') {
                    setStyleSettings(prev => ({ ...prev, btnRadius: value }));
                } else if (key === 'buttonPadding') {
                    setStyleSettings(prev => ({ ...prev, btnPadding: value }));
                } else if (key === 'shopHeroHeadingWeight' || key === 'shopHeroHeadingFontWeight') {
                    setStyleSettings(prev => ({ ...prev, shopHeroHeadingWeight: value, shopHeroHeadingFontWeight: value }));
                } else if (key === 'shopHeroSubheadingWeight' || key === 'shopHeroSubheadingFontWeight') {
                    setStyleSettings(prev => ({ ...prev, shopHeroSubheadingWeight: value, shopHeroSubheadingFontWeight: value }));
                }
            }

            return newData;
        });
    };

    const addFooterCol = (type = 'text') => {
        const newCol = type === 'socials'
            ? { heading: 'Follow Us', type: 'socials', width: 1 }
            : type === 'links'
            ? { heading: 'Quick Links', type: 'links', width: 1, links: [{ label: 'Home', url: '/' }] }
            : { heading: 'New Column', text: 'Add content here...', type: 'text', width: 1 };
        setData({ ...data, footer: [...(footer || []), newCol] });
    };

    const removeFooterCol = (fIndex) => {
        const newFooter = (footer || []).filter((_, i) => i !== fIndex);
        setData({ ...data, footer: newFooter });
    };

    const moveFooterCol = (fromIndex, toIndex) => {
        if (toIndex < 0 || toIndex >= (footer || []).length) return;
        const newFooter = [...(footer || [])];
        const [movedCol] = newFooter.splice(fromIndex, 1);
        newFooter.splice(toIndex, 0, movedCol);
        setData({ ...data, footer: newFooter });
    };

    // ── Shop Sections DnD & Management ──────────────────────────────────────
    const [showAddSectionPicker, setShowAddSectionPicker] = useState(false);
    const [activeSectionDragId, setActiveSectionDragId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8
            }
        }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const generateShopId = (prefix = 'sec-shop') => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    const updateShopSections = (updater) => {
        setData(prev => {
            const currentSections = prev?.shopSections || [];
            const nextSections = typeof updater === 'function' ? updater(currentSections) : updater;
            return {
                ...prev,
                shopSections: nextSections
            };
        });
    };

    const preparedShopSections = useMemo(() => {
        const rawSections = data?.shopSections || [];
        return rawSections.map((s, sIdx) => {
            const secId = s.id || s.stableId || `shop-sec-${sIdx}`;
            return {
                ...s,
                id: secId,
                stableId: secId,
                columns: (s.columns || []).map((c, cIdx) => {
                    const colId = c.id || c.stableId || `col-${secId}-${cIdx}`;
                    return {
                        ...c,
                        id: colId,
                        stableId: colId
                    };
                })
            };
        });
    }, [data?.shopSections]);

    const handleSectionDragStart = (event) => {
        setActiveSectionDragId(event.active.id);
    };

    const handleSectionDragOver = () => {};

    const handleSectionDragEnd = (event) => {
        const { active, over } = event;
        setActiveSectionDragId(null);
        if (!over || active.id === over.id) return;

        const currentPrepared = preparedShopSections;
        const oldSecIdx = currentPrepared.findIndex(s => s.stableId === active.id);
        const newSecIdx = currentPrepared.findIndex(s => s.stableId === over.id);

        if (oldSecIdx !== -1 && newSecIdx !== -1) {
            updateShopSections(secs => arrayMove([...secs], oldSecIdx, newSecIdx));
        } else {
            const sectionIdx = currentPrepared.findIndex(s =>
                (s.columns || []).some(c => c.stableId === active.id)
            );
            if (sectionIdx !== -1) {
                const sec = currentPrepared[sectionIdx];
                const oldColIdx = (sec.columns || []).findIndex(c => c.stableId === active.id);
                const newColIdx = (sec.columns || []).findIndex(c => c.stableId === over.id);
                if (oldColIdx !== -1 && newColIdx !== -1) {
                    updateShopSections(secs => {
                        const newSections = [...secs];
                        const target = { ...newSections[sectionIdx] };
                        target.columns = arrayMove([...(target.columns || [])], oldColIdx, newColIdx);
                        newSections[sectionIdx] = target;
                        return newSections;
                    });
                }
            }
        }
    };

    const deleteShopSection = (sectionId) => {
        if (window.confirm('Are you sure you want to delete this section?')) {
            updateShopSections(secs => secs.filter((s, sIdx) => {
                const secId = s.id || s.stableId || `sec-${sIdx}`;
                return secId !== sectionId && s.id !== sectionId && s.stableId !== sectionId;
            }));
        }
    };

    const deleteShopColumn = (sectionId, columnId) => {
        if (window.confirm('Are you sure you want to delete this column/item?')) {
            updateShopSections(secs => secs.map((s, sIdx) => {
                const secId = s.id || s.stableId || `sec-${sIdx}`;
                if (secId !== sectionId && s.id !== sectionId && s.stableId !== sectionId) return s;
                return {
                    ...s,
                    columns: (s.columns || []).filter((c, cIdx) => {
                        const colId = c.id || c.stableId || `col-${secId}-${cIdx}`;
                        return colId !== columnId && c.id !== columnId && c.stableId !== columnId;
                    })
                };
            }));
        }
    };

    const duplicateShopSection = (sectionId) => {
        const secs = data?.shopSections || [];
        const idx = secs.findIndex((s, sIdx) => (s.id || s.stableId || `sec-${sIdx}`) === sectionId || s.id === sectionId);
        if (idx === -1) return;
        const copy = JSON.parse(JSON.stringify(secs[idx]));
        copy.id = generateShopId('sec-shop');
        copy.stableId = copy.id;
        if (copy.columns) {
            copy.columns = copy.columns.map(c => ({
                ...c,
                id: generateShopId('col-shop'),
                stableId: generateShopId('col-shop')
            }));
        }
        updateShopSections(prev => {
            const updated = [...prev];
            updated.splice(idx + 1, 0, copy);
            return updated;
        });
    };

    const moveShopSection = (sectionId, direction) => {
        const secs = data?.shopSections || [];
        const idx = secs.findIndex((s, sIdx) => (s.id || s.stableId || `sec-${sIdx}`) === sectionId || s.id === sectionId);
        if (idx === -1) return;
        if (direction === 'up' && idx <= 0) return;
        if (direction === 'down' && idx >= secs.length - 1) return;
        const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
        updateShopSections(prev => arrayMove([...prev], idx, targetIdx));
    };

    const addShopColumn = (sectionId, colProps = {}) => {
        updateShopSections(secs => secs.map((s, sIdx) => {
            const secId = s.id || s.stableId || `sec-${sIdx}`;
            if (secId !== sectionId && s.id !== sectionId && s.stableId !== sectionId) return s;
            const newColId = generateShopId('col-shop');
            const newCol = {
                id: newColId,
                stableId: newColId,
                heading: colProps.heading !== undefined ? colProps.heading : 'New Feature',
                text: colProps.text !== undefined ? colProps.text : 'Add description here...',
                width: colProps.width || 4,
                ...colProps
            };
            return { ...s, columns: [...(s.columns || []), newCol] };
        }));
    };

    const addImageToShopGallery = (sectionId) => {
        updateShopSections(secs => secs.map((s, sIdx) => {
            const secId = s.id || s.stableId || `sec-${sIdx}`;
            if (secId !== sectionId && s.id !== sectionId && s.stableId !== sectionId) return s;
            const newCol = {
                id: generateShopId('col-shop'),
                stableId: generateShopId('col-shop'),
                heading: 'New Image',
                text: 'Add description...',
                image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800'
            };
            return { ...s, columns: [...(s.columns || []), newCol] };
        }));
    };

    const resizeShopColumn = (sectionId, columnId, w) => {
        updateShopSections(secs => secs.map((s, sIdx) => {
            const secId = s.id || s.stableId || `sec-${sIdx}`;
            if (secId !== sectionId && s.id !== sectionId && s.stableId !== sectionId) return s;
            return {
                ...s,
                columns: (s.columns || []).map((c, cIdx) => {
                    const colId = c.id || c.stableId || `col-${secId}-${cIdx}`;
                    if (colId === columnId || c.id === columnId) {
                        return { ...c, width: w };
                    }
                    return c;
                })
            };
        }));
    };

    const handleAddShopSectionTemplate = (templateKey) => {
        const tmpl = SHOP_SECTION_TEMPLATES.find(t => t.key === templateKey);
        if (!tmpl) return;
        const newSection = tmpl.create(generateShopId);
        updateShopSections(secs => [...secs, newSection]);
        setShowAddSectionPicker(false);
        setTimeout(() => {
            const el = document.getElementById(newSection.id);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 150);
    };

    const updateProductField = (productId, field, value) => {
        isProductsDirtyRef.current = true;
        setProducts(prevProducts => prevProducts.map(p => {
            if (p.id === productId) {
                const updated = { ...p, [field]: value };
                if (field === 'category') {
                    const rawParts = typeof value === 'string'
                        ? value.split(',').map(s => s.trim()).filter(Boolean)
                        : (Array.isArray(value) ? value : [value].filter(Boolean));
                    updated.categories = rawParts;
                } else if (field === 'categories') {
                    const rawList = Array.isArray(value)
                        ? value.map(c => typeof c === 'string' ? c.trim() : c?.name).filter(Boolean)
                        : [];
                    updated.categories = rawList;
                    if (!updated.category || !rawList.includes(updated.category)) {
                        updated.category = rawList[0] || '';
                    }
                }
                return updated;
            }
            return p;
        }));
    };

    const [dbCategories, setDbCategories] = useState(() => {
        try {
            const cached = localStorage.getItem(getTenantStorageKey('cached_categories'));
            if (cached) {
                const parsed = JSON.parse(cached);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed.map(normalizeProduct);
            }
        } catch (e) {}
        return [];
    });
    const [activeSubCategory, setActiveSubCategory] = useState('All');

    // --- CATALOG VIEW MODE STATE (Grid / List / Contractor Table) ---
    const [catalogViewMode, setCatalogViewMode] = useState(() => {
        try {
            return localStorage.getItem(getTenantStorageKey('catalogViewMode')) || 'grid';
        } catch (e) {
            return 'grid';
        }
    });
    const [tableQuantities, setTableQuantities] = useState({});
    const [tableSelectedOptions, setTableSelectedOptions] = useState({});
    const [expandedRowIds, setExpandedRowIds] = useState({});
    const [quickOrderModalProduct, setQuickOrderModalProduct] = useState(null);
    const [addedRowIds, setAddedRowIds] = useState({});
    const [batchAddedMessage, setBatchAddedMessage] = useState('');
    const [quickOrderPage, setQuickOrderPage] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const GRID_PAGE_SIZE = 24;
    const QUICK_ORDER_PAGE_SIZE = 25;

    // Reset pagination when search query, active category, subcategory, or sorting changes
    useEffect(() => {
        setCurrentPage(1);
        setQuickOrderPage(1);
    }, [search, activeCategory, activeSubCategory, sortBy]);

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        const anchor = document.getElementById('shop-catalog-anchor');
        if (anchor) {
            anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleQuickOrderPageChange = (newPage) => {
        setQuickOrderPage(newPage);
        const anchor = document.getElementById('shop-catalog-anchor');
        if (anchor) {
            anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const getNormalizedAttributes = (product) => {
        return extractProductAttributes(product);
    };

    const getSelectableAttributes = (product) => {
        const allAttrs = getNormalizedAttributes(product);
        const selectable = {};
        Object.entries(allAttrs).forEach(([k, opts]) => {
            if (Array.isArray(opts) && opts.length > 1) {
                selectable[k] = opts;
            }
        });
        return selectable;
    };

    const getEffectiveSelectedOptions = (product) => {
        if (!product) return {};
        const selectableAttrs = getSelectableAttributes(product);
        const result = {};
        const pId = product.id ?? product.rawId ?? product._id;
        const userSelected = (pId !== undefined ? (tableSelectedOptions[pId] || tableSelectedOptions[String(pId)]) : null)
            || (product.id !== undefined ? (tableSelectedOptions[product.id] || tableSelectedOptions[String(product.id)]) : null)
            || (product.rawId !== undefined ? (tableSelectedOptions[product.rawId] || tableSelectedOptions[String(product.rawId)]) : null)
            || {};
        
        Object.entries(selectableAttrs).forEach(([attrName, optionsList]) => {
            if (userSelected[attrName] !== undefined) {
                result[attrName] = userSelected[attrName];
            } else if (optionsList && optionsList.length > 0) {
                result[attrName] = optionsList[0];
            }
        });
        return result;
    };

    const toggleRowExpand = (productId) => {
        setExpandedRowIds(prev => ({
            ...prev,
            [productId]: !prev[productId]
        }));
    };

    const getMatchedVariation = (product, selectedOptions) => {
        return findMatchingVariation(product, selectedOptions);
    };

    const handleOptionChange = (productId, attrName, value) => {
        setTableSelectedOptions(prev => {
            const next = { ...prev };
            const existing = prev[productId] || (productId !== undefined ? prev[String(productId)] : {}) || {};
            const updated = {
                ...existing,
                [attrName]: value
            };
            next[productId] = updated;
            const strId = String(productId);
            if (strId !== productId) {
                next[strId] = updated;
            }
            return next;
        });
    };

    const handleTableQtyChange = (productId, qty) => {
        const validQty = Math.max(0, parseInt(qty, 10) || 0);
        setTableQuantities(prev => ({ ...prev, [productId]: validQty }));
    };

    const handleTableAddToCart = (product, customQty = null, customOptions = null) => {
        const qty = customQty !== null ? Math.max(1, customQty) : Math.max(1, tableQuantities[product.id] || 1);
        const effectiveOptions = customOptions !== null ? customOptions : getEffectiveSelectedOptions(product);
        const hasOptions = Object.keys(effectiveOptions).length > 0;
        const matchedVar = getMatchedVariation(product, effectiveOptions);

        addToCart(product, qty, hasOptions ? effectiveOptions : null, matchedVar);
        setAddedRowIds(prev => ({ ...prev, [product.id]: true }));
        setTimeout(() => {
            setAddedRowIds(prev => ({ ...prev, [product.id]: false }));
        }, 1500);
    };

    const renderPagination = (page, totalPages, onPageChange, totalItems, pageSize, compact = false) => {
        if (totalPages <= 1) return null;
        const startItem = (page - 1) * pageSize + 1;
        const endItem = Math.min(page * pageSize, totalItems);

        return (
            <div 
                className="shop-pagination-bar"
                style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: compact ? '4px' : '6px', 
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    userSelect: 'none'
                }}
            >
                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => onPageChange(Math.max(1, page - 1))}
                    style={{ 
                        padding: compact ? '3px 7px' : '6px 12px', 
                        border: '1px solid #cbd5e1', 
                        borderRadius: '6px', 
                        background: page <= 1 ? '#f8fafc' : '#ffffff', 
                        color: page <= 1 ? '#cbd5e1' : '#374151', 
                        fontWeight: 'bold', 
                        cursor: page <= 1 ? 'default' : 'pointer', 
                        fontSize: compact ? '0.72rem' : '0.82rem',
                        transition: 'all 0.15s ease'
                    }}
                >‹ Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= (compact ? 1 : 2))
                    .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                        acc.push(p);
                        return acc;
                    }, [])
                    .map((p, i) => p === '…'
                        ? <span key={`e${i}`} style={{ padding: '0 3px', color: '#94a3b8', fontSize: compact ? '0.72rem' : '0.82rem' }}>…</span>
                        : <button
                            key={p}
                            type="button"
                            onClick={() => onPageChange(p)}
                            style={{ 
                                minWidth: compact ? '24px' : '32px',
                                padding: compact ? '3px 6px' : '6px 10px', 
                                border: '1px solid', 
                                borderColor: p === page ? (styleSettings.primaryColor || '#3b82f6') : '#cbd5e1', 
                                borderRadius: '6px', 
                                background: p === page ? (styleSettings.primaryColor || '#3b82f6') : '#ffffff', 
                                color: p === page ? '#ffffff' : '#374151', 
                                fontWeight: p === page ? 'bold' : 'normal', 
                                cursor: 'pointer', 
                                fontSize: compact ? '0.72rem' : '0.82rem',
                                transition: 'all 0.15s ease'
                            }}
                        >{p}</button>
                    )
                }
                <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                    style={{ 
                        padding: compact ? '3px 7px' : '6px 12px', 
                        border: '1px solid #cbd5e1', 
                        borderRadius: '6px', 
                        background: page >= totalPages ? '#f8fafc' : '#ffffff', 
                        color: page >= totalPages ? '#cbd5e1' : '#374151', 
                        fontWeight: 'bold', 
                        cursor: page >= totalPages ? 'default' : 'pointer', 
                        fontSize: compact ? '0.72rem' : '0.82rem',
                        transition: 'all 0.15s ease'
                    }}
                >Next ›</button>
                <span style={{ fontSize: compact ? '0.68rem' : '0.78rem', color: '#64748b', whiteSpace: 'nowrap', marginLeft: '4px' }}>
                    {startItem}–{endItem} of {totalItems}
                </span>
            </div>
        );
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get(getApiUrl(`api_ecommerce.php?action=categories&t=${Date.now()}`), authHeaders());
            if (Array.isArray(res.data) && res.data.length > 0) {
                setDbCategories(res.data);
                try { localStorage.setItem(getTenantStorageKey('cached_categories'), JSON.stringify(res.data)); } catch (e) {}
            } else {
                try {
                    const staticRes = await axios.get(getApiUrl(`categories.sample.json?t=${Date.now()}`));
                    if (Array.isArray(staticRes.data) && staticRes.data.length > 0) {
                        setDbCategories(staticRes.data);
                        try { localStorage.setItem(getTenantStorageKey('cached_categories'), JSON.stringify(staticRes.data)); } catch (e) {}
                    }
                } catch (e) {}
            }
        } catch (e) {
            console.error("Failed to load categories from API, checking fallback", e);
            try {
                const staticRes = await axios.get(getApiUrl(`categories.sample.json?t=${Date.now()}`));
                if (Array.isArray(staticRes.data) && staticRes.data.length > 0) {
                    setDbCategories(staticRes.data);
                    try { localStorage.setItem(getTenantStorageKey('cached_categories'), JSON.stringify(staticRes.data)); } catch (err) {}
                }
            } catch (err) {
                const cached = localStorage.getItem(getTenantStorageKey('cached_categories'));
                if (cached) {
                    try { setDbCategories(JSON.parse(cached)); } catch (ce) {}
                }
            }
        }
    };

    const fetchProducts = async () => {
        try {
            const res = await axios.get(getApiUrl(`api_ecommerce.php?action=products&t=${Date.now()}`), authHeaders());
            if (Array.isArray(res.data) && res.data.length > 0) {
                const normalized = res.data.map(normalizeProduct);
                setProducts(normalized);
                try { localStorage.setItem(getTenantStorageKey('cached_products'), JSON.stringify(normalized)); } catch (e) {}
            } else {
                try {
                    const staticRes = await axios.get(getApiUrl(`products.sample.json?t=${Date.now()}`));
                    if (Array.isArray(staticRes.data)) {
                        const normalized = staticRes.data.map(normalizeProduct);
                        setProducts(normalized);
                        try { localStorage.setItem(getTenantStorageKey('cached_products'), JSON.stringify(normalized)); } catch (e) {}
                    }
                } catch (e) {}
            }
        } catch (e) {
            console.error("Failed to load products from API, falling back to products.sample.json", e);
            try {
                const staticRes = await axios.get(getApiUrl(`products.sample.json?t=${Date.now()}`));
                if (Array.isArray(staticRes.data)) {
                    setProducts(staticRes.data);
                    try { localStorage.setItem(getTenantStorageKey('cached_products'), JSON.stringify(staticRes.data)); } catch (e) {}
                }
            } catch (err) {
                console.error("Failed to load fallback products.sample.json", err);
            }
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchCategories();
        fetchProducts();
    }, []);

    const handleStartEditing = () => {
        setEditSnapshot({
            data: JSON.parse(JSON.stringify(data || {})),
            products: JSON.parse(JSON.stringify(products || [])),
            dbCategories: JSON.parse(JSON.stringify(dbCategories || [])),
            styleSettings: { ...styleSettings }
        });
        setIsEditing(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const sanitizedProducts = products.map(p => ({
                ...p,
                price: parseFloat(p.price) || 0
            }));
            await safePost(getApiUrl('api_ecommerce.php?action=products'), sanitizedProducts, authHeaders());
            try { localStorage.setItem(getTenantStorageKey('cached_products'), JSON.stringify(sanitizedProducts)); } catch (e) {}

            await safePost(getApiUrl('api_ecommerce.php?action=categories'), dbCategories, authHeaders());
            try { localStorage.setItem(getTenantStorageKey('cached_categories'), JSON.stringify(dbCategories)); } catch (e) {}

            // Persist all page edits and settings to api.php safely
            if (onSaveConfig) {
                const mergedSettings = {
                    ...(config?.settings || {}),
                    ...styleSettings,
                    ...(data?.settings || {})
                };

                const chosenBg = (data?.settings?.pageBackgroundImage && data?.settings?.pageBackgroundImage.trim()) ||
                                 (data?.settings?.shopPageBgImage && data?.settings?.shopPageBgImage.trim()) ||
                                 (styleSettings.pageBackgroundImage && styleSettings.pageBackgroundImage.trim()) ||
                                 (styleSettings.shopPageBgImage && styleSettings.shopPageBgImage.trim()) ||
                                 '';
                if (!chosenBg) {
                    mergedSettings.pageBackgroundImage = '';
                    mergedSettings.shopPageBgImage = '';
                    mergedSettings.productPageBgImage = '';
                } else {
                    mergedSettings.pageBackgroundImage = chosenBg;
                    mergedSettings.shopPageBgImage = chosenBg;
                    mergedSettings.productPageBgImage = chosenBg;
                }

                const newConfig = {
                    ...(config || {}),
                    ...(data || {}),
                    settings: mergedSettings,
                    shopHero: data?.shopHero || shopHero,
                    modules: {
                        ...(config?.modules || {}),
                        ...(data?.modules || {})
                    }
                };
                await onSaveConfig(newConfig);
                setData(newConfig);
            }
            
            setEditSnapshot(null);
            setIsEditing(false);
        } catch (err) {
            console.error("Failed to save changes", err);
            alert("Error saving shop page changes.");
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (editSnapshot) {
            if (editSnapshot.data) setData(editSnapshot.data);
            if (editSnapshot.products) setProducts(editSnapshot.products);
            if (editSnapshot.dbCategories) setDbCategories(editSnapshot.dbCategories);
            if (editSnapshot.styleSettings) setStyleSettings(editSnapshot.styleSettings);
        } else {
            if (config) setData(config);
            fetchProducts();
            fetchCategories();
        }
        setEditSnapshot(null);
        setIsEditing(false);
    };

    const handleAddProductLive = () => {
        isProductsDirtyRef.current = true;
        const defaultCat = activeCategory !== 'All' ? activeCategory : (dbCategories.length > 0 ? dbCategories[0].name : 'General');
        const defaultSubCat = activeSubCategory !== 'All' ? activeSubCategory : undefined;
        const newProd = {
            id: 'prod_' + Date.now(),
            name: 'New Product',
            description: 'Enter product description here...',
            price: 25.00,
            category: defaultCat,
            subCategory: defaultSubCat,
            stock: 1,
            images: ['./public/hero_abstract_bg.png'],
            inquiryOnly: false,
            pageConfig: {
                layout: 'split',
                imageAlign: 'left',
                pageBg: '',
                cardBg: '',
                titleColor: '',
                priceColor: '',
                descColor: '',
                sections: []
            }
        };
        setProducts(prev => [newProd, ...prev]);
    };

    const handleDuplicateProductLive = (prod) => {
        isProductsDirtyRef.current = true;
        const duplicate = {
            ...prod,
            id: 'prod_' + Date.now(),
            name: 'Copy of ' + prod.name,
            pageConfig: prod.pageConfig
                ? { ...prod.pageConfig, sections: JSON.parse(JSON.stringify(prod.pageConfig.sections || [])) }
                : { layout: 'split', imageAlign: 'left', pageBg: '', cardBg: '', titleColor: '', priceColor: '', descColor: '', sections: [] }
        };
        setProducts(prev => [duplicate, ...prev]);
    };

    const handleDeleteProductLive = (prodId) => {
        if (!window.confirm("Are you sure you want to delete this product?")) return;
        isProductsDirtyRef.current = true;
        setProducts(prev => prev.filter(p => p.id !== prodId));
    };

    const handleSectionSelect = (sectionId) => {
        if (!showCustomizer) {
            setShowCustomizer(true);
        }
        setSelectedSimpleSection(sectionId || null);
        if (sectionId) {
            if (['heroTitle', 'typography'].includes(sectionId)) {
                setCustomizerTab('typography');
            } else if (['cards', 'buttonStyle'].includes(sectionId)) {
                setCustomizerTab('card');
            } else if (['categories', 'filterSettings'].includes(sectionId)) {
                setCustomizerTab('nav');
            } else if (['presets', 'pageColors', 'bgImage'].includes(sectionId)) {
                setCustomizerTab('theme');
            } else if (['mobile', 'responsive'].includes(sectionId)) {
                setCustomizerTab('mobile');
            } else if (['customcss'].includes(sectionId)) {
                setCustomizerTab('customcss');
            }
        }
    };

    const renderSimpleSectionWrapper = (sectionId, children, title) => {
        if (!isEditing || !showCustomizer || !isSimpleMode) return children;
        const isSelected = selectedSimpleSection === sectionId;
        return (
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    handleSectionSelect(sectionId);
                }}
                className="simple-edit-clickable-section"
                style={{
                    position: 'relative',
                    cursor: 'pointer',
                    outline: isSelected ? '2px dashed #2563eb' : '1px dashed rgba(37,99,235,0.45)',
                    outlineOffset: '3px',
                    borderRadius: '8px',
                    transition: 'all 0.18s ease'
                }}
                title={title || `Click to customize ${sectionId}`}
            >
                {children}
            </div>
        );
    };

    const handleOpenCustomizer = () => {
        setSavedStyleSnapshot({ ...styleSettings });
        setShowCustomizer(true);
        setCustomizerTrigger(Date.now());
    };

    const handleCancelStyles = () => {
        if (savedStyleSnapshot) {
            setStyleSettings(savedStyleSnapshot);
        }
        setShowCustomizer(false);
    };

    const handleResetStyles = () => setStyleSettings({ ...SS_DEFAULTS });

    const handleStyleChange = (key, val) => {
        setStyleSettings(prev => {
            const next = { ...prev, [key]: val };
            if (key === 'pageBackgroundImage' || key === 'shopPageBgImage') {
                next.pageBackgroundImage = val;
                next.shopPageBgImage = val;
                next.productPageBgImage = val;
            }
            return next;
        });
        setData(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                settings: {
                    ...(prev?.settings || {}),
                    [key]: val,
                    ...(key === 'pageBackgroundImage' || key === 'shopPageBgImage' ? {
                        pageBackgroundImage: val,
                        shopPageBgImage: val,
                        productPageBgImage: val
                    } : {})
                }
            };
        });
    };

    const handleSaveStyles = async () => {
        if (!onSaveConfig) return;
        setSaving(true);
        try {
            // 1. Save all product edits to ecommerce backend (only if products were modified)
            if (isProductsDirtyRef.current && Array.isArray(products) && products.length > 0) {
                try {
                    const sanitizedProducts = products.map(p => ({
                        ...p,
                        price: parseFloat(p.price) || 0
                    }));
                    await safePost(getApiUrl('api_ecommerce.php?action=products'), sanitizedProducts, authHeaders());
                    try { localStorage.setItem(getTenantStorageKey('cached_products'), JSON.stringify(sanitizedProducts)); } catch (e) {}
                    isProductsDirtyRef.current = false;
                } catch (prodErr) {
                    console.error("Failed to save products on style save", prodErr);
                }
            }

            // 2. Save all categories to ecommerce backend (only if categories were modified)
            if (isCategoriesDirtyRef.current && Array.isArray(dbCategories) && dbCategories.length > 0) {
                try {
                    await safePost(getApiUrl('api_ecommerce.php?action=categories'), dbCategories, authHeaders());
                    try { localStorage.setItem(getTenantStorageKey('cached_categories'), JSON.stringify(dbCategories)); } catch (e) {}
                    isCategoriesDirtyRef.current = false;
                } catch (catErr) {
                    console.error("Failed to save categories on style save", catErr);
                }
            }

            // 3. Build merged settings combining config, data, and active styleSettings
            const chosenBg = (styleSettings.pageBackgroundImage && styleSettings.pageBackgroundImage.trim()) ||
                             (styleSettings.shopPageBgImage && styleSettings.shopPageBgImage.trim()) ||
                             '';

            const newSettings = {
                ...(config?.settings || {}),
                ...(data?.settings || {}),
                ...styleSettings,
                pageBackgroundImage: chosenBg,
                shopPageBgImage: chosenBg,
                productPageBgImage: chosenBg,
                pageBackgroundRepeat: styleSettings.pageBackgroundRepeat || 'no-repeat',
                pageBackgroundSize: styleSettings.pageBackgroundSize || 'cover',
                pageBackgroundPosition: styleSettings.pageBackgroundPosition || 'center',
                pageBackgroundAttachment: styleSettings.pageBackgroundAttachment || 'scroll',
                pageBgOverlayColor: styleSettings.pageBgOverlayColor || '#000000',
                pageBgOverlayOpacity: styleSettings.pageBgOverlayOpacity !== undefined ? styleSettings.pageBgOverlayOpacity : 0,
                ...(styleSettings.primaryColor ? { primaryColor: styleSettings.primaryColor } : {}),
                ...(styleSettings.pageBackground ? {
                    pageBackground: styleSettings.pageBackground,
                    shopPageBgColor: styleSettings.pageBackground
                } : {})
            };

            // 4. Save ALL page edits and settings!
            // Merge config, data, newSettings, shopHero, and modules so nothing is ever lost
            const newConfig = {
                ...(config || {}),
                ...(data || {}),
                settings: newSettings,
                shopHero: data?.shopHero || shopHero,
                modules: {
                    ...(config?.modules || {}),
                    ...(data?.modules || {})
                }
            };

            await onSaveConfig(newConfig);

            // Update local memory state
            setData(newConfig);
            setEditSnapshot(null);
            setSavedStyleSnapshot({ ...styleSettings });
            setShowCustomizer(false);
        } catch (err) {
            console.error("Failed to save shop styles:", err);
            alert("Error saving shop settings. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // Route category slug handling
    const routeCategorySlug = params.categorySlug || params.slug || params['*'];
    const searchParams = new URLSearchParams(location.search);
    const categorySlug = searchParams.get('category') || searchParams.get('cat');
    const effectiveSlugParam = routeCategorySlug || categorySlug;

    // Listen for category in URL search params or route param
    useEffect(() => {
        let catParam = null;
        let subCatParam = null;
        if (effectiveSlugParam) {
            catParam = decodeURIComponent(effectiveSlugParam);
        } else if (location?.search) {
            const sp = new URLSearchParams(location.search);
            catParam = sp.get('category') || sp.get('cat');
            subCatParam = sp.get('subcategory') || sp.get('subcat');
        }

        if (catParam) {
            const rawClean = catParam.trim();
            const matchedDbCat = dbCategories.find(c => 
                c.name.toLowerCase() === rawClean.toLowerCase() || 
                (c.slug && c.slug.toLowerCase() === rawClean.toLowerCase()) ||
                (c.id && c.id.toLowerCase() === rawClean.toLowerCase()) ||
                slugify(c.name) === slugify(rawClean)
            );

            if (matchedDbCat) {
                if (matchedDbCat.parentId) {
                    const parent = dbCategories.find(p => p.id === matchedDbCat.parentId);
                    if (parent) {
                        setActiveCategory(parent.name);
                        setActiveSubCategory(matchedDbCat.name);
                    } else {
                        setActiveCategory(matchedDbCat.name);
                        setActiveSubCategory('All');
                    }
                } else {
                    setActiveCategory(matchedDbCat.name);
                    setActiveSubCategory('All');
                }
            } else {
                // Match against products (which may have categories even when dbCategories has entries)
                const matchedProd = products.find(p => {
                    const cats = getProductCategoriesList(p);
                    return cats.some(c => c.toLowerCase() === rawClean.toLowerCase() || slugify(c) === slugify(rawClean)) ||
                        (p.category && (p.category.toLowerCase() === rawClean.toLowerCase() || slugify(p.category) === slugify(rawClean))) ||
                        (p.subCategory && (p.subCategory.toLowerCase() === rawClean.toLowerCase() || slugify(p.subCategory) === slugify(rawClean)));
                });

                if (matchedProd) {
                    if (matchedProd.subCategory && (matchedProd.subCategory.toLowerCase() === rawClean.toLowerCase() || slugify(matchedProd.subCategory) === slugify(rawClean))) {
                        setActiveCategory(matchedProd.category || 'All');
                        setActiveSubCategory(matchedProd.subCategory);
                    } else {
                        const cats = getProductCategoriesList(matchedProd);
                        const matchedCat = cats.find(c => c.toLowerCase() === rawClean.toLowerCase() || slugify(c) === slugify(rawClean));
                        setActiveCategory(matchedCat || matchedProd.category || rawClean);
                        setActiveSubCategory('All');
                    }
                } else if (rawClean.toLowerCase() === 'all' || rawClean.toLowerCase() === 'shop') {
                    setActiveCategory('All');
                    setActiveSubCategory('All');
                } else {
                    // Fall back to rawClean category name directly
                    setActiveCategory(rawClean);
                    setActiveSubCategory('All');
                }
            }
        } else if (!effectiveSlugParam && !location?.search) {
            setActiveCategory('All');
            setActiveSubCategory('All');
        }

        if (subCatParam) {
            const rawSubClean = subCatParam.trim();
            const matchedSubCat = dbCategories.find(c => 
                c.name.toLowerCase() === rawSubClean.toLowerCase() || 
                (c.slug && c.slug.toLowerCase() === rawSubClean.toLowerCase()) ||
                slugify(c.name) === slugify(rawSubClean)
            );
            if (matchedSubCat) {
                setActiveSubCategory(matchedSubCat.name);
            } else {
                const matchedProdSub = products.find(p =>
                    p.subCategory && (p.subCategory.toLowerCase() === rawSubClean.toLowerCase() || slugify(p.subCategory) === slugify(rawSubClean))
                );
                setActiveSubCategory(matchedProdSub ? matchedProdSub.subCategory : rawSubClean);
            }
        }
    }, [location?.search, effectiveSlugParam, dbCategories, products]);



    const isProductMatchingCategory = (product, targetCat, subCatsOfTarget = []) => {
        if (!targetCat || targetCat === 'All') return true;
        if (!product) return false;
        const allCats = getProductCategoriesList(product);
        if (allCats.length === 0) return false;
        if (allCats.some(c => isCategoryMatch(c, targetCat))) return true;
        if (subCatsOfTarget.length > 0 && allCats.some(c => subCatsOfTarget.some(sub => isCategoryMatch(c, sub.name)))) {
            return true;
        }
        return false;
    };

    const isProductMatchingSubCategory = (product, targetSubCat) => {
        if (!targetSubCat || targetSubCat === 'All') return true;
        if (!product) return false;
        const allCats = getProductCategoriesList(product);
        if (allCats.length === 0) return false;
        return allCats.some(c => isSubCategoryMatch(c, targetSubCat));
    };

    const isCategoryHidden = (c) => {
        if (!c) return false;
        const hVal = typeof c === 'object' ? c.hidden : null;
        if (hVal === true || hVal === 1 || hVal === '1' || hVal === 'true') return true;
        if (hVal === false || hVal === 0 || hVal === '0' || hVal === 'false') return false;
        const norm = (typeof c === 'string' ? c : (c.name || '')).toString().trim().toLowerCase();
        const slugStr = (typeof c === 'object' && c.slug ? c.slug : slugify(norm)).toString().trim().toLowerCase();
        if (norm === 'uncategorized' || norm === 'products' || slugStr === 'uncategorized' || slugStr === 'products') return true;
        return false;
    };

    const hiddenCatNames = new Set();
    dbCategories.forEach(c => {
        if (isCategoryHidden(c)) {
            if (c.name) hiddenCatNames.add(c.name.trim().toLowerCase());
            if (c.slug) hiddenCatNames.add(c.slug.trim().toLowerCase());
            hiddenCatNames.add(slugify(c.name || ''));
        }
    });
    hiddenCatNames.add('uncategorized');
    hiddenCatNames.add('products');

    const draftProductCount = useMemo(() => {
        return products.filter(p => p.hidden || p.status === 'draft' || p.status === 'hidden').length;
    }, [products]);

    const hiddenCatCount = useMemo(() => {
        return dbCategories.filter(c => isCategoryHidden(c)).length;
    }, [dbCategories]);

    const handleToggleHideCategory = (catNameOrId) => {
        if (!catNameOrId) return;
        const norm = String(typeof catNameOrId === 'object' ? catNameOrId.name : catNameOrId).trim().toLowerCase();
        if (norm === 'all') {
            if (update) {
                update(['settings', 'shopCatHideAll'], !Boolean(settings?.shopCatHideAll || styleSettings?.shopCatHideAll));
            }
            return;
        }
        const catStr = typeof catNameOrId === 'object' ? (catNameOrId.id || catNameOrId.name) : catNameOrId;
        
        setDbCategories(prev => {
            const existingIdx = prev.findIndex(c => 
                (c.id && c.id === catStr) || 
                (c.name && c.name.toLowerCase() === norm) || 
                (c.slug && c.slug.toLowerCase() === norm)
            );
            if (existingIdx >= 0) {
                const currentHidden = isCategoryHidden(prev[existingIdx]);
                const updated = [...prev];
                updated[existingIdx] = {
                    ...updated[existingIdx],
                    hidden: currentHidden ? 0 : 1
                };
                return updated;
            } else {
                const name = typeof catNameOrId === 'object' ? catNameOrId.name : catNameOrId;
                const newCat = {
                    id: `cat_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
                    name: name,
                    slug: slugify(name),
                    hidden: 1,
                    color: '#3b82f6',
                    description: '',
                    parentId: null
                };
                return [...prev, newCat];
            }
        });
    };

    const handleEditCategory = (catNameOrId, updatedFields = {}) => {
        if (!catNameOrId) return;
        const catStr = typeof catNameOrId === 'object' ? (catNameOrId.id || catNameOrId.name) : catNameOrId;
        const norm = String(typeof catNameOrId === 'object' ? catNameOrId.name : catNameOrId).trim().toLowerCase();

        setDbCategories(prev => {
            const existingIdx = prev.findIndex(c => 
                (c.id && c.id === catStr) || 
                (c.name && c.name.toLowerCase() === norm) || 
                (c.slug && c.slug.toLowerCase() === norm)
            );

            if (existingIdx >= 0) {
                const oldName = prev[existingIdx].name;
                const updated = [...prev];
                updated[existingIdx] = {
                    ...updated[existingIdx],
                    ...updatedFields
                };
                if (updatedFields.name && updatedFields.name !== oldName) {
                    setProducts(prodList => prodList.map(p => {
                        let changed = false;
                        let pCopy = { ...p };
                        if (pCopy.category === oldName) {
                            pCopy.category = updatedFields.name;
                            changed = true;
                        }
                        if (Array.isArray(pCopy.categories) && pCopy.categories.includes(oldName)) {
                            pCopy.categories = pCopy.categories.map(c => c === oldName ? updatedFields.name : c);
                            changed = true;
                        }
                        if (pCopy.subCategory === oldName) {
                            pCopy.subCategory = updatedFields.name;
                            changed = true;
                        }
                        return changed ? pCopy : p;
                    }));
                    if (activeCategory === oldName) {
                        setActiveCategory(updatedFields.name);
                    }
                }
                return updated;
            } else {
                const baseName = updatedFields.name || (typeof catNameOrId === 'object' ? catNameOrId.name : catNameOrId);
                const newCat = {
                    id: `cat_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
                    name: baseName,
                    slug: slugify(baseName),
                    hidden: 0,
                    color: '#3b82f6',
                    description: '',
                    parentId: null,
                    ...updatedFields
                };
                return [...prev, newCat];
            }
        });
    };

    const handleCreateCategory = (name, parentId = null, extraFields = {}) => {
        if (!name || !name.trim()) return null;
        const trimmed = name.trim();
        const newCat = {
            id: `cat_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
            name: trimmed,
            slug: slugify(trimmed),
            description: '',
            image: '',
            color: '#3b82f6',
            parentId: parentId || null,
            hidden: 0,
            ...extraFields
        };
        setDbCategories(prev => [...prev, newCat]);
        return newCat;
    };

    const handleDeleteCategory = (catNameOrId) => {
        if (!catNameOrId || catNameOrId === 'All') return;
        const catStr = typeof catNameOrId === 'object' ? (catNameOrId.id || catNameOrId.name) : catNameOrId;
        const norm = String(typeof catNameOrId === 'object' ? catNameOrId.name : catNameOrId).trim().toLowerCase();

        setDbCategories(prev => prev.filter(c => 
            c.id !== catStr && 
            (c.name ? c.name.toLowerCase() !== norm : true) && 
            (c.slug ? c.slug.toLowerCase() !== norm : true)
        ));
    };

    const allCategoryNames = useMemo(() => {
        const set = new Set();
        // 1. All from dbCategories
        dbCategories.forEach(c => {
            if (!c.parentId && c.name) {
                if ((isEditing && showUnpublishedInEdit) || !isCategoryHidden(c)) {
                    set.add(c.name);
                }
            }
        });
        // 2. All from products
        products.forEach(p => {
            const isProdDraftOrHidden = (p.hidden || p.status === 'draft' || p.status === 'hidden');
            if (isProdDraftOrHidden && (!isEditing || !showUnpublishedInEdit)) {
                return;
            }
            const cats = getProductCategoriesList(p);
            cats.forEach(cat => {
                if (cat) {
                    const norm = cat.trim().toLowerCase();
                    const isHidden = hiddenCatNames.has(norm) || hiddenCatNames.has(slugify(norm));
                    if ((isEditing && showUnpublishedInEdit) || !isHidden) {
                        set.add(cat.trim());
                    }
                }
            });
        });
        return Array.from(set);
    }, [dbCategories, products, isEditing, showUnpublishedInEdit, hiddenCatNames]);
    
    const uniqueCategoryMap = new Map();
    allCategoryNames.forEach(cat => {
        if (!cat) return;
        const key = slugify(cat);
        if (!uniqueCategoryMap.has(key)) {
            uniqueCategoryMap.set(key, cat);
        }
    });

    const catSortMode = styleSettings.shopCatSort || settings.shopCatSort || 'custom';
    const customOrderList = Array.isArray(styleSettings.shopCategoryOrder) && styleSettings.shopCategoryOrder.length > 0
        ? styleSettings.shopCategoryOrder 
        : (Array.isArray(settings.shopCategoryOrder) ? settings.shopCategoryOrder : []);

    const sortedCategoryList = useMemo(() => {
        const list = Array.from(uniqueCategoryMap.values());
        if (catSortMode === 'alpha-desc') {
            return list.sort((a, b) => b.localeCompare(a, undefined, { sensitivity: 'base' }));
        }
        if (catSortMode === 'alpha') {
            return list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        }
        // Custom sort order (by customOrderList or dbCategories array order)
        if (customOrderList.length > 0) {
            return list.sort((a, b) => {
                const idxA = customOrderList.indexOf(a);
                const idxB = customOrderList.indexOf(b);
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b, undefined, { sensitivity: 'base' });
            });
        }
        if (Array.isArray(dbCategories) && dbCategories.length > 0) {
            const dbNames = dbCategories.map(c => c.name).filter(Boolean);
            return list.sort((a, b) => {
                const idxA = dbNames.indexOf(a);
                const idxB = dbNames.indexOf(b);
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b, undefined, { sensitivity: 'base' });
            });
        }
        return list.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [uniqueCategoryMap, catSortMode, customOrderList, dbCategories]);

    const handleReorderCategories = (newOrderList) => {
        if (!Array.isArray(newOrderList)) return;
        isCategoriesDirtyRef.current = true;
        
        const cleanOrder = newOrderList.map(item => 
            typeof item === 'string' ? item : (item?.name || String(item || ''))
        ).filter(Boolean);

        let reorderedDb = dbCategories;
        if (Array.isArray(dbCategories) && dbCategories.length > 0) {
            reorderedDb = [...dbCategories].sort((a, b) => {
                const idxA = cleanOrder.indexOf(a.name);
                const idxB = cleanOrder.indexOf(b.name);
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return 0;
            });
            setDbCategories(reorderedDb);
        }

        setStyleSettings(prev => ({
            ...prev,
            shopCatSort: 'custom',
            shopCategoryOrder: cleanOrder
        }));

        if (update) {
            update(['settings', 'shopCatSort'], 'custom');
            update(['settings', 'shopCategoryOrder'], cleanOrder);
        }

        // 1. Save reordered dbCategories directly to backend API & localStorage cache
        if (Array.isArray(reorderedDb) && reorderedDb.length > 0) {
            safePost(getApiUrl('api_ecommerce.php?action=categories'), reorderedDb, authHeaders())
                .catch(err => console.error("Error persisting reordered categories to API:", err));
            try { localStorage.setItem(getTenantStorageKey('cached_categories'), JSON.stringify(reorderedDb)); } catch (e) {}
        }

        // 2. Persist shopCategoryOrder & shopCatSort to tenant config
        if (onSaveConfig) {
            const updatedConfig = {
                settings: {
                    ...(config?.settings || {}),
                    ...(styleSettings || {}),
                    shopCatSort: 'custom',
                    shopCategoryOrder: cleanOrder
                }
            };
            onSaveConfig(updatedConfig).catch(err => console.error("Error saving category order config:", err));
        }
    };

    const handleMoveCategory = (catOrName, direction) => {
        const catName = typeof catOrName === 'string' 
            ? catOrName 
            : (catOrName?.name || catOrName?.originalName || String(catOrName || ''));

        const list = [...sortedCategoryList];
        
        let idx = list.indexOf(catName);
        if (idx === -1) {
            const normTarget = catName.trim().toLowerCase();
            idx = list.findIndex(c => String(c).trim().toLowerCase() === normTarget || slugify(String(c)) === slugify(normTarget));
        }

        if (idx === -1) return;

        let targetIdx = idx;
        if (direction === 'up' || direction === 'left') targetIdx = idx - 1;
        else if (direction === 'down' || direction === 'right') targetIdx = idx + 1;
        else if (direction === 'top') targetIdx = 0;
        else if (direction === 'bottom') targetIdx = list.length - 1;

        if (targetIdx < 0 || targetIdx >= list.length || targetIdx === idx) return;

        const [movedItem] = list.splice(idx, 1);
        list.splice(targetIdx, 0, movedItem);

        handleReorderCategories(list);
    };

    const handleShuffleCategories = () => {
        const rawList = [...sortedCategoryList];
        for (let i = rawList.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [rawList[i], rawList[j]] = [rawList[j], rawList[i]];
        }
        handleReorderCategories(rawList);
    };

    const handleSortCategoriesAlpha = (descending = false) => {
        const list = [...sortedCategoryList];
        list.sort((a, b) => descending 
            ? b.localeCompare(a, undefined, { sensitivity: 'base' })
            : a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
        handleReorderCategories(list);
        const sortVal = descending ? 'alpha-desc' : 'alpha';
        setStyleSettings(prev => ({ ...prev, shopCatSort: sortVal }));
        if (update) {
            update(['settings', 'shopCatSort'], sortVal);
        }
    };

    const categories = ['All', ...sortedCategoryList];

    const activeParentCatObj = activeCategory !== 'All'
        ? dbCategories.find(c => !c.parentId && (c.name === activeCategory || slugify(c.name) === slugify(activeCategory) || isCategoryMatch(c.name, activeCategory)))
        : null;
    const activeSubCatObj = activeSubCategory !== 'All' 
        ? dbCategories.find(c => (c.name === activeSubCategory || slugify(c.name) === slugify(activeSubCategory) || isSubCategoryMatch(c.name, activeSubCategory)) && (c.parentId === activeParentCatObj?.id || !c.parentId)) 
        : null;
    const directCatMatch = activeCategory !== 'All'
        ? dbCategories.find(c => (c.name === activeCategory || slugify(c.name) === slugify(activeCategory) || isCategoryMatch(c.name, activeCategory) || isCategoryMatch(c.slug, activeCategory) || isCategoryMatch(c.id, activeCategory)))
        : null;
    const currentCategoryObj = activeSubCatObj || activeParentCatObj || directCatMatch || (activeCategory !== 'All' ? { name: activeCategory } : null);

    const subCategoriesOfActive = activeParentCatObj 
        ? dbCategories.filter(c => c.parentId === activeParentCatObj.id && ((isEditing && showUnpublishedInEdit) || !c.hidden)) 
        : [];

    const filteredProducts = useMemo(() => {
        return products.filter(p => {
            const isProdDraftOrHidden = (p.hidden || p.status === 'draft' || p.status === 'hidden');
            if (isProdDraftOrHidden && (!isEditing || !showUnpublishedInEdit)) {
                return false;
            }
            if ((!isEditing || !showUnpublishedInEdit) && isCategoryMatch(activeCategory, 'All')) {
                const pCats = getProductCategoriesList(p);
                if (pCats.length > 0 && pCats.every(c => hiddenCatNames.has(c))) {
                    return false;
                }
            }
            const q = search.trim().toLowerCase();
            const pCats = getProductCategoriesList(p);
            const matchesSearch = !q || 
                (p.name && p.name.toLowerCase().includes(q)) || 
                (p.description && p.description.toLowerCase().includes(q)) ||
                (pCats.some(c => c.toLowerCase().includes(q))) ||
                (p.category && p.category.toLowerCase().includes(q)) ||
                (p.subCategory && p.subCategory.toLowerCase().includes(q));

            const matchesCategory = isProductMatchingCategory(p, activeCategory, subCategoriesOfActive);
            const matchesSubCategory = isProductMatchingSubCategory(p, activeSubCategory);

            return matchesSearch && matchesCategory && matchesSubCategory;
        });
    }, [products, search, activeCategory, activeSubCategory, subCategoriesOfActive, isEditing, showUnpublishedInEdit, hiddenCatNames]);

    const sortedProducts = useMemo(() => {
        const list = [...filteredProducts];
        if (sortBy === 'category') {
            list.sort((a, b) => {
                const catA = (a.category || a.subCategory || '').toString().trim().toLowerCase();
                const catB = (b.category || b.subCategory || '').toString().trim().toLowerCase();
                const catComp = catA.localeCompare(catB, undefined, { sensitivity: 'base' });
                if (catComp !== 0) return catComp;
                const nameA = (a.name || '').toString().trim().toLowerCase();
                const nameB = (b.name || '').toString().trim().toLowerCase();
                return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
            });
        } else if (sortBy === 'name-asc') {
            list.sort((a, b) => (a.name || '').toString().localeCompare((b.name || '').toString(), undefined, { sensitivity: 'base' }));
        } else if (sortBy === 'name-desc') {
            list.sort((a, b) => (b.name || '').toString().localeCompare((a.name || '').toString(), undefined, { sensitivity: 'base' }));
        } else if (sortBy === 'price-asc') {
            list.sort((a, b) => getMinProductPrice(a) - getMinProductPrice(b));
        } else if (sortBy === 'price-desc') {
            list.sort((a, b) => getMinProductPrice(b) - getMinProductPrice(a));
        } else if (sortBy === 'newest') {
            list.sort((a, b) => {
                const idA = String(a.id || '');
                const idB = String(b.id || '');
                return idB.localeCompare(idA);
            });
        }
        return list;
    }, [filteredProducts, sortBy]);

    const activePageBgImg = styleSettings.pageBackgroundImage !== undefined
        ? styleSettings.pageBackgroundImage
        : (styleSettings.shopPageBgImage !== undefined
            ? styleSettings.shopPageBgImage
            : (settings.pageBackgroundImage || settings.shopPageBgImage || ''));

    const activeBgRepeat = styleSettings.pageBackgroundRepeat || settings.pageBackgroundRepeat || 'no-repeat';
    const activeBgSize = styleSettings.pageBackgroundSize || settings.pageBackgroundSize || 'cover';
    const activeBgPos = styleSettings.pageBackgroundPosition || settings.pageBackgroundPosition || 'center';
    const activeBgAttachment = styleSettings.pageBackgroundAttachment || settings.pageBackgroundAttachment || 'scroll';
    const activeBgOverlayColor = styleSettings.pageBgOverlayColor || settings.pageBgOverlayColor || '#000000';
    const activeBgOverlayOpacity = (styleSettings.pageBgOverlayOpacity !== undefined)
        ? styleSettings.pageBgOverlayOpacity
        : (settings.pageBgOverlayOpacity !== undefined ? settings.pageBgOverlayOpacity : 0);
    const overlayAlpha = (parseFloat(activeBgOverlayOpacity) || 0) / 100;
    const activeBgOverlayRgba = overlayAlpha > 0 ? hexToRgba(activeBgOverlayColor, overlayAlpha) : null;

    const effectiveSettings = {
        ...(settings || {}),
        ...styleSettings,
        pageBackgroundImage: activePageBgImg,
        shopPageBgImage: activePageBgImg,
        productPageBgImage: activePageBgImg,
        pageBackgroundRepeat: activeBgRepeat,
        pageBackgroundSize: activeBgSize,
        pageBackgroundPosition: activeBgPos,
        pageBackgroundAttachment: activeBgAttachment,
        pageBgOverlayColor: activeBgOverlayColor,
        pageBgOverlayOpacity: activeBgOverlayOpacity,
        ...(settings.headerBgColor ? { headerBgColor: settings.headerBgColor } : (styleSettings.secondaryColor ? { headerBgColor: styleSettings.secondaryColor } : {})),
        ...(settings.footerBgColor ? { footerBgColor: settings.footerBgColor } : (styleSettings.secondaryColor ? { footerBgColor: styleSettings.secondaryColor } : {})),
        ...(styleSettings.primaryColor ? {
            primaryColor: styleSettings.primaryColor
        } : {}),
        ...(styleSettings.pageBackground ? {
            pageBackground: styleSettings.pageBackground,
            shopPageBgColor: styleSettings.pageBackground
        } : {})
    };

    const effectiveConfig = {
        ...(data || {}),
        settings: effectiveSettings
    };

    const primaryColor = styleSettings.primaryColor || effectiveSettings.primaryColor || '#3b82f6';
    const secondaryColor = styleSettings.secondaryColor || effectiveSettings.secondaryColor || '#1e293b';

    const _getFontStyles = getFontStyles(config?.typography || typography || {});
    const _getShadow = getShadow(settings);
    const _getSideBorder = getSideBorder(settings);
    const _getBtnColor = getBtnColor(effectiveSettings);
    const _getBtnTextColor = getBtnTextColor(effectiveSettings);
    const _resolveBg = resolveSectionBg(effectiveSettings);
    const _getBackground = getBackground(effectiveSettings);
    const btnStyleBase = buildBtnStyleBase(effectiveSettings, typography);
    const imgStyleBase = buildImgStyleBase(effectiveSettings);

    const responsiveCss = useMemo(() => {
        const deskCols = catalogViewMode === 'list' ? 1 : (parseInt(styleSettings.shopGridColumns) || 3);
        const tabCols = catalogViewMode === 'list' ? 1 : (parseInt(styleSettings.shopGridTabletColumns) || Math.min(deskCols, 2));
        const mobCols = catalogViewMode === 'list' ? 1 : (parseInt(styleSettings.shopGridMobileColumns) || 1);
        const deskGap = styleSettings.shopGridGap || '30px';
        const tabGap = styleSettings.shopGridTabletGap || styleSettings.shopGridMobileGap || '20px';
        const mobGap = styleSettings.shopGridMobileGap || '16px';

        return `
        .shop-search-sort-row {
            display: flex !important;
            align-items: center !important;
            box-sizing: border-box !important;
        }
        .shop-search-input-wrap,
        .shop-sort-select-wrap {
            display: inline-flex !important;
            align-items: center !important;
            align-self: center !important;
            box-sizing: border-box !important;
            margin: 0 !important;
        }
        .shop-search-input,
        .shop-sort-select {
            display: inline-block !important;
            vertical-align: middle !important;
            box-sizing: border-box !important;
            margin: 0 !important;
        }
        .shop-products-grid {
            display: grid;
            grid-template-columns: repeat(${deskCols}, minmax(0, 1fr));
            gap: ${addUnit(deskGap)};
        }

        /* Shop Product Card Image Transition & Hover Interaction */
        ${(() => {
            const cardImgHover = styleSettings.shopCardImageHoverFilter || 'none';
            const cardImgDuration = styleSettings.shopCardImageTransitionDuration || '0.4s';
            const cardImgTiming = styleSettings.shopCardImageTransitionTiming || 'cubic-bezier(0.4, 0, 0.2, 1)';
            const cardImgTintOpacity = styleSettings.shopCardImageTintOpacity !== undefined ? parseFloat(styleSettings.shopCardImageTintOpacity) : 0.4;

            let rules = `
            .shop-product-card-img {
                transition: filter ${cardImgDuration} ${cardImgTiming}, -webkit-filter ${cardImgDuration} ${cardImgTiming}, transform ${cardImgDuration} ${cardImgTiming} !important;
                will-change: filter, transform;
            }
            .shop-card-image-tint-overlay {
                transition: opacity ${cardImgDuration} ${cardImgTiming}, background-color ${cardImgDuration} ${cardImgTiming} !important;
            }
            `;

            if (cardImgHover === 'color-on-hover') {
                rules += `
                .shop-product-card:hover .shop-product-card-img {
                    filter: none !important;
                    -webkit-filter: none !important;
                }
                .shop-product-card:hover .shop-card-image-tint-overlay {
                    opacity: 0 !important;
                }
                `;
            } else if (cardImgHover === 'zoom-color-on-hover' || cardImgHover === 'zoom-color') {
                rules += `
                .shop-product-card:hover .shop-product-card-img {
                    filter: none !important;
                    -webkit-filter: none !important;
                    transform: scale(1.05) !important;
                }
                .shop-product-card:hover .shop-card-image-tint-overlay {
                    opacity: 0 !important;
                }
                `;
            } else if (cardImgHover === 'desaturate-on-hover') {
                rules += `
                .shop-product-card:hover .shop-product-card-img {
                    filter: grayscale(100%) !important;
                    -webkit-filter: grayscale(100%) !important;
                }
                `;
            } else if (cardImgHover === 'boost-tint-on-hover') {
                rules += `
                .shop-product-card:hover .shop-card-image-tint-overlay {
                    opacity: ${Math.min(1, cardImgTintOpacity * 1.5 + 0.2)} !important;
                }
                `;
            } else if (cardImgHover && cardImgHover !== 'none') {
                rules += `
                .shop-product-card:hover .shop-product-card-img {
                    filter: ${cardImgHover} !important;
                    -webkit-filter: ${cardImgHover} !important;
                }
                `;
            }
            return rules;
        })()}
        @media (max-width: 1024px) {
            .shop-products-grid {
                grid-template-columns: repeat(${tabCols}, minmax(0, 1fr)) !important;
                gap: ${addUnit(tabGap)} !important;
            }
        }
        .shop-main-container {
            margin: ${addUnit(styleSettings.shopPageMargin || '0 auto')} !important;
            padding: ${addUnit(styleSettings.shopPagePadding || '0 24px 60px 24px')} !important;
            ${styleSettings.shopPagePaddingTop !== undefined && styleSettings.shopPagePaddingTop !== '' ? `padding-top: ${addUnit(styleSettings.shopPagePaddingTop)} !important;` : ''}
            ${styleSettings.shopPagePaddingRight !== undefined && styleSettings.shopPagePaddingRight !== '' ? `padding-right: ${addUnit(styleSettings.shopPagePaddingRight)} !important;` : ''}
            ${styleSettings.shopPagePaddingBottom !== undefined && styleSettings.shopPagePaddingBottom !== '' ? `padding-bottom: ${addUnit(styleSettings.shopPagePaddingBottom)} !important;` : ''}
            ${styleSettings.shopPagePaddingLeft !== undefined && styleSettings.shopPagePaddingLeft !== '' ? `padding-left: ${addUnit(styleSettings.shopPagePaddingLeft)} !important;` : ''}
        }
        ${(styleSettings.shopHeroHeadingWeight || settings.shopHeroHeadingWeight || settings.shopHeroHeadingFontWeight || shopHero?.titleFontWeight || shopHero?.titleWeight) ? `.shop-hero-h1 { font-weight: ${styleSettings.shopHeroHeadingWeight || settings.shopHeroHeadingWeight || settings.shopHeroHeadingFontWeight || shopHero?.titleFontWeight || shopHero?.titleWeight} !important; }` : ''}
        ${(styleSettings.shopHeroSubheadingWeight || settings.shopHeroSubheadingWeight || settings.shopHeroSubheadingFontWeight || shopHero?.subtitleFontWeight || shopHero?.subtitleWeight) ? `.shop-hero-subheading { font-weight: ${styleSettings.shopHeroSubheadingWeight || settings.shopHeroSubheadingWeight || settings.shopHeroSubheadingFontWeight || shopHero?.subtitleFontWeight || shopHero?.subtitleWeight} !important; }` : ''}
        ${(settings.shopHeroHeadingSize || shopHero?.titleSize || shopHero?.titleFontSize) ? `.shop-hero-h1 { font-size: ${addUnit(settings.shopHeroHeadingSize || shopHero?.titleSize || shopHero?.titleFontSize)} !important; }` : ''}
        ${(settings.shopHeroSubheadingSize || shopHero?.subtitleSize || shopHero?.subtitleFontSize) ? `.shop-hero-subheading { font-size: ${addUnit(settings.shopHeroSubheadingSize || shopHero?.subtitleSize || shopHero?.subtitleFontSize)} !important; }` : ''}
        @media (max-width: 768px) {
            .shop-main-container {
                margin: ${addUnit(styleSettings.shopPageMobileMargin || '24px auto')} !important;
                padding: ${addUnit(styleSettings.shopPageMobilePadding || '0 14px')} !important;
                ${styleSettings.shopPageMobilePaddingTop !== undefined && styleSettings.shopPageMobilePaddingTop !== '' ? `padding-top: ${addUnit(styleSettings.shopPageMobilePaddingTop)} !important;` : ''}
            }
            .shop-products-grid {
                grid-template-columns: repeat(${mobCols}, minmax(0, 1fr)) !important;
                gap: ${addUnit(mobGap)} !important;
            }
            .shop-product-card {
                ${styleSettings.shopCardMobileDirection ? `flex-direction: ${styleSettings.shopCardMobileDirection} !important;` : ''}
                ${styleSettings.shopCardMobileHeight ? `height: ${styleSettings.shopCardMobileHeight === 'auto' ? 'auto' : addUnit(styleSettings.shopCardMobileHeight)} !important;` : ''}
                ${styleSettings.shopCardMobileMinHeight ? `min-height: ${addUnit(styleSettings.shopCardMobileMinHeight)} !important;` : ''}
                ${styleSettings.shopCardMobileMaxHeight ? `max-height: ${addUnit(styleSettings.shopCardMobileMaxHeight)} !important;` : ''}
            }
            .shop-product-card-body {
                padding: ${addUnit(styleSettings.shopCardMobilePadding || (mobCols >= 2 ? '12px' : '16px'))} !important;
                ${styleSettings.shopCardContentMobileGap !== undefined && styleSettings.shopCardContentMobileGap !== '' ? `gap: ${addUnit(styleSettings.shopCardContentMobileGap)} !important;` : ''}
            }
            .shop-product-card-img-wrap {
                ${styleSettings.shopCardImageMobileWidth ? `width: ${addUnit(styleSettings.shopCardImageMobileWidth)} !important;` : ''}
                ${styleSettings.shopImageMobilePadding !== undefined && styleSettings.shopImageMobilePadding !== '' ? `padding: ${addUnit(styleSettings.shopImageMobilePadding)} !important;` : ''}
                ${styleSettings.shopImageMobileBorderRadius !== undefined && styleSettings.shopImageMobileBorderRadius !== '' ? `border-radius: ${addUnit(styleSettings.shopImageMobileBorderRadius)} !important;` : ''}
                ${styleSettings.shopCardImageMobileAspectRatio && styleSettings.shopCardImageMobileAspectRatio !== 'auto'
                    ? 'height: auto !important;'
                    : `height: ${styleSettings.shopCardImageMobileHeight === 'auto' ? 'auto' : addUnit(styleSettings.shopCardImageMobileHeight || (mobCols >= 2 ? '160px' : '220px'))} !important;`
                }
            }
            .shop-product-card-img-box {
                ${styleSettings.shopCardImageMobileAspectRatio && styleSettings.shopCardImageMobileAspectRatio !== 'auto'
                    ? `aspect-ratio: ${styleSettings.shopCardImageMobileAspectRatio} !important; height: auto !important;`
                    : (styleSettings.shopCardImageMobileHeight
                        ? `height: ${styleSettings.shopCardImageMobileHeight === 'auto' ? 'auto' : addUnit(styleSettings.shopCardImageMobileHeight)} !important;`
                        : '')
                }
            }
            ${styleSettings.shopCardImageMobileFit ? `.shop-product-card-img { object-fit: ${styleSettings.shopCardImageMobileFit} !important; }` : ''}
            .shop-product-action-row {
                ${styleSettings.shopCardMobileActionRowDirection ? `flex-direction: ${styleSettings.shopCardMobileActionRowDirection === 'column-center' ? 'column' : styleSettings.shopCardMobileActionRowDirection} !important;` : ''}
                ${styleSettings.shopCardMobileActionRowDirection === 'column-center' ? 'align-items: center !important;' : ''}
                ${styleSettings.shopCardActionRowMobileGap !== undefined && styleSettings.shopCardActionRowMobileGap !== '' ? `gap: ${addUnit(styleSettings.shopCardActionRowMobileGap)} !important;` : ''}
            }
            ${(() => {
                const mobLayout = styleSettings.shopCardMobileButtonsLayout;
                const deskLayout = styleSettings.shopCardButtonsLayout;
                const isStacked = mobLayout === 'column' || mobLayout === 'column-reverse' || mobLayout === 'stacked' || (deskLayout === 'column' && (!mobLayout || mobLayout === 'inherit')) || (deskLayout === 'column-reverse' && (!mobLayout || mobLayout === 'inherit'));
                const isReverse = mobLayout === 'column-reverse' || (deskLayout === 'column-reverse' && (!mobLayout || mobLayout === 'inherit'));
                const isRowEqual = mobLayout === 'row-equal' || (deskLayout === 'row-equal' && (!mobLayout || mobLayout === 'inherit'));
                const isRow = mobLayout === 'row' || (deskLayout === 'row' && (!mobLayout || mobLayout === 'inherit'));
                
                let rules = '';
                if (isStacked) {
                    rules += `
                    .shop-product-action-row {
                        flex-direction: column !important;
                        align-items: stretch !important;
                    }
                    .shop-card-buttons-wrapper {
                        flex-direction: ${isReverse ? 'column-reverse' : 'column'} !important;
                        width: 100% !important;
                        align-items: stretch !important;
                    }
                    .shop-card-buttons-wrapper > .shop-card-btn,
                    .shop-card-buttons-wrapper > .shop-card-qty-wrap {
                        width: 100% !important;
                        max-width: 100% !important;
                        flex: 0 0 auto !important;
                        box-sizing: border-box !important;
                    }
                    `;
                } else if (isRowEqual) {
                    rules += `
                    .shop-card-buttons-wrapper {
                        flex-direction: row !important;
                        width: 100% !important;
                    }
                    .shop-card-buttons-wrapper > .shop-card-btn {
                        flex: 1 1 0px !important;
                        width: auto !important;
                    }
                    `;
                } else if (isRow) {
                    rules += `
                    .shop-card-buttons-wrapper {
                        flex-direction: row !important;
                    }
                    `;
                }
                if (styleSettings.shopCardButtonsMobileGap) {
                    rules += `
                    .shop-card-buttons-wrapper {
                        gap: ${addUnit(styleSettings.shopCardButtonsMobileGap)} !important;
                    }
                    `;
                }
                return rules;
            })()}
            .shop-card-btn {
                ${styleSettings.shopCardBtnMobileHeight ? `height: ${addUnit(styleSettings.shopCardBtnMobileHeight)} !important;` : ''}
                ${styleSettings.shopCardBtnMobilePadding ? `padding: ${addUnit(styleSettings.shopCardBtnMobilePadding)} !important;` : ''}
                ${styleSettings.shopCardBtnMobileFontSize ? `font-size: ${addUnit(styleSettings.shopCardBtnMobileFontSize)} !important;` : ''}
            }
            .shop-card-qty-wrap {
                ${styleSettings.shopCardBtnMobileHeight ? `height: ${addUnit(styleSettings.shopCardBtnMobileHeight)} !important;` : ''}
            }
            ${styleSettings.shopHeroHeadingMobileSize ? `.shop-hero-h1, .shop-hero-h1 * { font-size: ${addUnit(styleSettings.shopHeroHeadingMobileSize)} !important; }` : ''}
            ${styleSettings.shopHeroHeadingMobileAlign ? `.shop-hero-h1, .shop-hero-h1 * { text-align: ${styleSettings.shopHeroHeadingMobileAlign} !important; }` : ''}
            ${styleSettings.shopProductNameMobileSize ? `.shop-product-title, .shop-product-title * { font-size: ${addUnit(styleSettings.shopProductNameMobileSize)} !important; }` : ''}
            ${styleSettings.shopProductNameMobileAlign ? `.shop-product-title, .shop-product-title * { text-align: ${styleSettings.shopProductNameMobileAlign} !important; }` : ''}
            ${styleSettings.shopProductPriceMobileSize ? `.shop-product-price { font-size: ${addUnit(styleSettings.shopProductPriceMobileSize)} !important; }` : ''}
            ${styleSettings.shopProductPriceMobileAlign ? `.shop-product-price { text-align: ${styleSettings.shopProductPriceMobileAlign} !important; }` : ''}
            ${styleSettings.shopProductDescMobileSize ? `.shop-product-desc, .shop-product-desc * { font-size: ${addUnit(styleSettings.shopProductDescMobileSize)} !important; }` : ''}
            ${styleSettings.shopProductDescMobileAlign ? `.shop-product-desc, .shop-product-desc * { text-align: ${styleSettings.shopProductDescMobileAlign} !important; }` : ''}
            ${styleSettings.shopProductCategoryMobileSize ? `.shop-product-category { font-size: ${addUnit(styleSettings.shopProductCategoryMobileSize)} !important; }` : ''}
            ${styleSettings.shopProductCategoryMobileAlign ? `.shop-product-category { text-align: ${styleSettings.shopProductCategoryMobileAlign} !important; align-self: ${styleSettings.shopProductCategoryMobileAlign === 'center' ? 'center' : (styleSettings.shopProductCategoryMobileAlign === 'right' ? 'flex-end' : 'flex-start')} !important; }` : ''}
            ${styleSettings.shopCatBtnMobileSize ? `.shop-cat-btn { font-size: ${addUnit(styleSettings.shopCatBtnMobileSize)} !important; }` : ''}
            ${styleSettings.shopCatBtnMobilePadding ? `.shop-cat-btn { padding: ${addUnit(styleSettings.shopCatBtnMobilePadding)} !important; }` : ''}
            ${styleSettings.shopCatBarMobileScroll === 'scroll' ? `
            .shop-cat-buttons-container {
                flex-wrap: nowrap !important;
                overflow-x: auto !important;
                -webkit-overflow-scrolling: touch;
                padding-bottom: 6px !important;
                justify-content: flex-start !important;
                scrollbar-width: thin;
            }
            .shop-cat-btn {
                flex-shrink: 0 !important;
            }
            ` : ''}
        }
        @media (max-width: 480px) {
            .shop-main-container {
                padding: ${addUnit(styleSettings.shopPageMobilePadding || '0 10px')} !important;
                ${styleSettings.shopPageMobilePaddingTop !== undefined && styleSettings.shopPageMobilePaddingTop !== '' ? `padding-top: ${addUnit(styleSettings.shopPageMobilePaddingTop)} !important;` : ''}
            }
            ${mobCols >= 2 ? `
            .shop-product-card-body {
                padding: ${addUnit(styleSettings.shopCardMobilePadding || '10px')} !important;
                gap: ${addUnit(styleSettings.shopCardContentMobileGap || '6px')} !important;
            }
            .shop-product-card-img-wrap {
                ${styleSettings.shopCardImageMobileAspectRatio && styleSettings.shopCardImageMobileAspectRatio !== 'auto'
                    ? 'height: auto !important;'
                    : `height: ${styleSettings.shopCardImageMobileHeight === 'auto' ? 'auto' : addUnit(styleSettings.shopCardImageMobileHeight || '140px')} !important;`
                }
            }
            ` : ''}
        }
        ${styleSettings.shopCustomCss || ''}
        `;
    }, [styleSettings, catalogViewMode]);

    return (
        <div style={{
            backgroundColor: styleSettings.pageBackground || effectiveSettings.pageBackground || '#f8fafc',
            backgroundImage: (activePageBgImg && effectiveSettings.pageBgMode !== 'gradient')
                ? (activeBgOverlayRgba
                    ? `linear-gradient(${activeBgOverlayRgba}, ${activeBgOverlayRgba}), url('${resolveMediaUrl(activePageBgImg)}')`
                    : `url('${resolveMediaUrl(activePageBgImg)}')`)
                : undefined,
            backgroundSize: activeBgSize,
            backgroundPosition: activeBgPos,
            backgroundRepeat: activeBgRepeat,
            backgroundAttachment: activeBgAttachment,
            color: '#0f172a',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Inter, system-ui, sans-serif'
        }}>
            <SeoHead
                title={`${currentCategoryObj ? (currentCategoryObj.metaTitle || currentCategoryObj.name) : (shopHero?.title || 'Shop & Catalog')} | ${settings?.businessName || 'Store'}`}
                description={currentCategoryObj ? (currentCategoryObj.metaDescription || currentCategoryObj.description || 'Browse products') : (shopHero?.subtitle || settings?.siteDescription || 'Browse products in store')}
                keywords={currentCategoryObj?.metaKeywords || settings?.siteKeywords}
                ogImage={shopHero?.backgroundImage || currentCategoryObj?.image || settings?.heroImage}
                config={data}
            />

            <GlobalStyles config={effectiveConfig} settings={effectiveSettings} modules={data?.modules || modules} siteSettings={data?.siteSettings} />
            <style dangerouslySetInnerHTML={{ __html: responsiveCss }} />

            {/* Custom injected CSS */}
            {styleSettings.shopCustomCss && (
                <style dangerouslySetInnerHTML={{ __html: styleSettings.shopCustomCss }} />
            )}

            {/* =========================================================================
              * 🛡️ ARCHITECTURAL PRESERVATION GUARD — HEADER (<SiteNav>)
              * DO NOT ALTER, TRUNCATE, OR HARDCODE CUSTOM PROPS FOR THIS HEADER.
              * MUST REMAIN 100% PARALLEL WITH LandingPage, ArticlesPage, ProductPage, etc.
              * ========================================================================= */}
            <SiteNav
                config={data}
                settings={settings}
                socials={data?.socials || socials}
                modules={data?.modules || modules}
                onSetConfig={setData}
                onSaveConfig={onSaveConfig}
                getFontStyles={_getFontStyles}
                getShadow={_getShadow}
                getSideBorder={_getSideBorder}
                anchorMode="link"
                onAnchorClick={(e, url) => {
                    e.preventDefault();
                    const anchorId = url.replace(/^#/, '');
                    // If the anchor target exists on the current shop page (e.g. a product card), scroll here
                    if (anchorId && findAnchorElement(anchorId)) {
                        smoothScrollTo(anchorId);
                    } else {
                        // Otherwise navigate to home page and let the scroll engine handle it there
                        navigate('/', { state: { targetId: anchorId } });
                    }
                }}
                isEditing={isEditing}
                isAuthenticated={isAuthenticated}
                cartCount={cartCount}
                titleEditable={<BrandEditable isEditing={isEditing} settings={settings} update={update} modules={data?.modules || modules} />}
            />

            {/* Header spacer — conditional when overlaying hero banner or when hero is hidden */}
            {Boolean(shopHero?.hideHero || !settings?.headerOverlayHero) && (
                <div className="header-spacer" style={{ height: headerHeight != null ? `${headerHeight}px` : addUnit(styleSettings.shopHeaderSpaceBelow || settings?.headerSpaceBelow || '80px') }}></div>
            )}

            {/* Rich Shop Hero Section with Background / Layout / Typography controls */}
            {renderSimpleSectionWrapper('heroTitle', (
                <HeroBlock
                    hero={shopHero}
                    settings={effectiveSettings}
                    isEditing={isEditing}
                    update={update}
                    heroPathPrefix={['shopHero']}
                    pageIndex="shop"
                    getFontStyles={_getFontStyles}
                    getBtnColor={_getBtnColor}
                    btnStyleBase={btnStyleBase}
                    imgStyleBase={imgStyleBase}
                />
            ), "Click to edit Hero Title & Settings")}

            {/* Main Shop Catalog Container */}
            <main 
                id="shop-catalog-anchor" 
                className="shop-main-container" 
                style={{ 
                    flexGrow: 1, 
                    padding: isMobile ? (styleSettings.shopPageMobilePadding || '0 16px 40px 16px') : (styleSettings.shopPagePadding || '0 24px 60px 24px'), 
                    ...(isMobile ? 
                        (styleSettings.shopPageMobilePaddingTop !== undefined && styleSettings.shopPageMobilePaddingTop !== '' ? { paddingTop: addUnit(styleSettings.shopPageMobilePaddingTop) } : {}) : 
                        (styleSettings.shopPagePaddingTop !== undefined && styleSettings.shopPagePaddingTop !== '' ? { paddingTop: addUnit(styleSettings.shopPagePaddingTop) } : {})
                    ),
                    maxWidth: styleSettings.shopPageMaxWidth || '1440px', 
                    width: '100%', 
                    margin: styleSettings.shopPageMargin || '0 auto', 
                    boxSizing: 'border-box' 
                }}
            >
                {modules?.shop === false ? (
                    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#64748b' }}>
                        <h2>Store currently unavailable</h2>
                        <p>The shop catalog has been disabled by the store administrator.</p>
                    </div>
                ) : (
                    <>
                        {/* ── Breadcrumb Navigation ── */}
                        {renderSimpleSectionWrapper('categories', (
                            <ShopBreadcrumbs
                                styleSettings={styleSettings}
                                settings={settings}
                                activeCategory={activeCategory}
                                activeSubCategory={activeSubCategory}
                                setActiveCategory={setActiveCategory}
                                setActiveSubCategory={setActiveSubCategory}
                                isCategoryMatch={isCategoryMatch}
                                isSubCategoryMatch={isSubCategoryMatch}
                                primaryColor={primaryColor}
                            />
                        ), "Click to edit Categories & Navigation")}

                        {/* Category & Hero Placement */}
                        {(() => {
                            const heroEl = renderSimpleSectionWrapper('heroTitle', (
                                <ShopHero
                                    hero={shopHero}
                                    shopHero={shopHero}
                                    isHeroBlockVisible={!shopHero?.hideHero}
                                    isEditing={isEditing}
                                    update={update}
                                    settings={settings}
                                    typography={typography}
                                    styleSettings={styleSettings}
                                    isMobile={isMobile}
                                    activeCategory={activeCategory}
                                    activeSubCategory={activeSubCategory}
                                    currentCategoryObj={currentCategoryObj}
                                    directCatMatch={directCatMatch}
                                    primaryColor={primaryColor}
                                    secondaryColor={secondaryColor}
                                    dbCategories={dbCategories}
                                    setDbCategories={setDbCategories}
                                    setProducts={setProducts}
                                    handleToggleHideCategory={handleToggleHideCategory}
                                    handleEditCategory={handleEditCategory}
                                    effectiveSlugParam={effectiveSlugParam}
                                    navigate={navigate}
                                    _getFontStyles={_getFontStyles}
                                />
                            ), "Click to edit Hero Title & Settings");

                            const getFilterBarEl = (hideCategories = false) => renderSimpleSectionWrapper('categories', (
                                <ShopFilterBar
                                    styleSettings={styleSettings}
                                    search={search}
                                    setSearch={setSearch}
                                    searchQuery={search}
                                    setSearchQuery={setSearch}
                                    sortBy={sortBy}
                                    setSortBy={setSortBy}
                                    categories={categories}
                                    activeCategory={activeCategory}
                                    setActiveCategory={setActiveCategory}
                                    activeSubCategory={activeSubCategory}
                                    setActiveSubCategory={setActiveSubCategory}
                                    subCategoriesOfActive={subCategoriesOfActive}
                                    hiddenCatNames={hiddenCatNames}
                                    isCategoryMatch={isCategoryMatch}
                                    isSubCategoryMatch={isSubCategoryMatch}
                                    hideCategories={hideCategories}
                                    settings={settings}
                                    typography={typography}
                                    isEditing={isEditing}
                                    showUnpublishedInEdit={showUnpublishedInEdit}
                                    dbCategories={dbCategories}
                                    setDbCategories={setDbCategories}
                                    handleToggleHideCategory={handleToggleHideCategory}
                                    handleEditCategory={handleEditCategory}
                                    handleCreateCategory={handleCreateCategory}
                                    handleDeleteCategory={handleDeleteCategory}
                                    isMobile={isMobile}
                                    isTablet={isTablet}
                                    catalogViewMode={catalogViewMode}
                                    setCatalogViewMode={setCatalogViewMode}
                                    handleAddProductLive={handleAddProductLive}
                                    handleOpenCustomizer={handleOpenCustomizer}
                                    isAuthenticated={isAuthenticated}
                                    handleShuffleCategories={handleShuffleCategories}
                                    handleReorderCategories={handleReorderCategories}
                                    handleMoveCategory={handleMoveCategory}
                                    handleSortCategoriesAlpha={handleSortCategoriesAlpha}
                                    productsCount={sortedProducts.length}
                                    primaryColor={primaryColor}
                                    navigate={navigate}
                                    update={update}
                                    _getFontStyles={_getFontStyles}
                                    pages={data?.pages || config?.pages || []}
                                    products={products}
                                />
                            ), "Click to edit Categories Bar & Filters");

                            if (styleSettings.shopCategoriesPlacement === 'above-heading') {
                                return (
                                    <>
                                        {getFilterBarEl(false)}
                                        {heroEl}
                                    </>
                                );
                            }
                            if (styleSettings.shopCategoriesPlacement === 'hidden') {
                                return (
                                    <>
                                        {heroEl}
                                        {getFilterBarEl(true)}
                                    </>
                                );
                            }
                            return (
                                <>
                                    {heroEl}
                                    {getFilterBarEl(false)}
                                </>
                            );
                        })()}

                        {/* Live Edit Mode Visibility Alert Banner */}
                        {isEditing && !showUnpublishedInEdit && (draftProductCount > 0 || hiddenCatCount > 0) && (
                            <div style={{
                                background: '#fffbeb',
                                border: '1px solid #fef3c7',
                                borderLeft: '4px solid #f59e0b',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                marginBottom: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '8px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', color: '#92400e' }}>
                                    <LuEyeOff size={16} style={{ color: '#d97706', flexShrink: 0 }} />
                                    <span>
                                        <strong>Live Edit Preview Active:</strong> {draftProductCount} unpublished/draft product{draftProductCount === 1 ? '' : 's'} and {hiddenCatCount} hidden categor{hiddenCatCount === 1 ? 'y' : 'ies'} are currently hidden from view.
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowUnpublishedInEdit(true)}
                                    style={{
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        background: '#ffffff',
                                        border: '1px solid #f59e0b',
                                        color: '#b45309',
                                        fontSize: '0.74rem',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}
                                >
                                    <LuEye size={13} />
                                    <span>Show Unpublished Items</span>
                                </button>
                            </div>
                        )}

                        {/* Product Grid & List Views */}
                        <ShopProductGrid
                            loading={loading}
                            catalogViewMode={catalogViewMode}
                            sortedProducts={sortedProducts}
                            tableQuantities={tableQuantities}
                            handleTableQtyChange={handleTableQtyChange}
                            tableSelectedOptions={tableSelectedOptions}
                            handleOptionChange={handleOptionChange}
                            expandedRowIds={expandedRowIds}
                            toggleRowExpand={toggleRowExpand}
                            addedRowIds={addedRowIds}
                            handleTableAddToCart={handleTableAddToCart}
                            addToCart={handleTableAddToCart || addToCart}
                            addBatchToCart={addBatchToCart}
                            batchAddedMessage={batchAddedMessage}
                            setBatchAddedMessage={setBatchAddedMessage}
                            quickOrderModalProduct={quickOrderModalProduct}
                            setQuickOrderModalProduct={setQuickOrderModalProduct}
                            getSelectableAttributes={getSelectableAttributes}
                            getNormalizedAttributes={getNormalizedAttributes}
                            getEffectiveSelectedOptions={getEffectiveSelectedOptions}
                            getMatchedVariation={getMatchedVariation}
                            quickOrderPage={quickOrderPage}
                            handleQuickOrderPageChange={handleQuickOrderPageChange}
                            QUICK_ORDER_PAGE_SIZE={QUICK_ORDER_PAGE_SIZE}
                            currentPage={currentPage}
                            handlePageChange={handlePageChange}
                            GRID_PAGE_SIZE={GRID_PAGE_SIZE}
                            renderPagination={renderPagination}
                            isEditing={isEditing}
                            updateProductField={updateProductField}
                            handleDuplicateProductLive={handleDuplicateProductLive}
                            handleDeleteProductLive={handleDeleteProductLive}
                            onEditProductDetails={setExpandedProductModal}
                            styleSettings={styleSettings}
                            settings={settings}
                            typography={typography}
                            formatPrice={formatPrice}
                            primaryColor={primaryColor}
                            secondaryColor={secondaryColor}
                            isMobile={isMobile}
                            isTablet={isTablet}
                            search={search}
                            activeCategory={activeCategory}
                            activeSubCategory={activeSubCategory}
                            setSearch={setSearch}
                            setActiveCategory={setActiveCategory}
                            setActiveSubCategory={setActiveSubCategory}
                            handleAddProductLive={handleAddProductLive}
                            dbCategories={dbCategories}
                            update={update}
                            _getFontStyles={_getFontStyles}
                            isCommercialMode={isCommercialMode}
                            isSimpleMode={isEditing && showCustomizer && isSimpleMode}
                            selectedSimpleSection={selectedSimpleSection}
                            onSectionSelect={handleSectionSelect}
                        />
                    </>
                )}
            </main>

            {/* Sections below catalog grid */}
            {preparedShopSections.length > 0 && (
                <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
                    <Suspense fallback={null}>
                        <SectionCanvas
                            data={data}
                            setData={setData}
                            isEditing={isEditing}
                            settings={effectiveSettings}
                            typography={data?.typography || typography || {}}
                            getFontStyles={_getFontStyles}
                            getBackground={_getBackground}
                            resolveSectionBg={_resolveBg}
                            getBtnColor={_getBtnColor}
                            getBtnTextColor={_getBtnTextColor}
                            getShadow={_getShadow}
                            getSideBorder={_getSideBorder}
                            addUnit={addUnit}
                            imgStyleBase={imgStyleBase}
                            btnStyleBase={btnStyleBase}
                            generateId={generateShopId}
                            pathPrefix={['shopSections']}
                            deleteSection={deleteShopSection}
                            deleteColumn={deleteShopColumn}
                            update={update}
                            duplicateSection={duplicateShopSection}
                            moveSection={moveShopSection}
                            addColumn={addShopColumn}
                            addImageToGallery={addImageToShopGallery}
                            resizeColumn={resizeShopColumn}
                            sensors={sensors}
                            handleDragStart={handleSectionDragStart}
                            handleDragOver={handleSectionDragOver}
                            handleDragEnd={handleSectionDragEnd}
                            preparedSections={preparedShopSections}
                            showAddButtons={false}
                        />
                    </Suspense>
                </div>
            )}

            {/* In-place Add Section UI below catalog grid (Admin Edit Mode only) */}
            {isEditing && (
                <div
                    id="shop-add-section-container"
                    style={{
                        padding: '40px 20px 60px',
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 1,
                        width: '100%',
                        boxSizing: 'border-box'
                    }}
                >
                    {!showAddSectionPicker ? (
                        <button
                            type="button"
                            onClick={() => setShowAddSectionPicker(true)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '12px 28px',
                                background: '#ffffff',
                                color: '#1e293b',
                                border: '1.5px dashed #94a3b8',
                                borderRadius: '10px',
                                fontSize: '14px',
                                fontWeight: '600',
                                letterSpacing: '0.01em',
                                cursor: 'pointer',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.04)',
                                transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.borderColor = '#2563eb';
                                e.currentTarget.style.color = '#2563eb';
                                e.currentTarget.style.background = '#f8faff';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(37,99,235,0.12)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.borderColor = '#94a3b8';
                                e.currentTarget.style.color = '#1e293b';
                                e.currentTarget.style.background = '#ffffff';
                                e.currentTarget.style.boxShadow = '0 2px 5px rgba(0,0,0,0.04)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <LuPlus size={16} />
                            <span>Add Section Below Catalog</span>
                        </button>
                    ) : (
                        <div style={{
                            maxWidth: '980px',
                            margin: '0 auto',
                            background: '#ffffff',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 20px 40px -15px rgba(0,0,0,0.1), 0 4px 10px -2px rgba(0,0,0,0.05)',
                            padding: '28px 32px 32px',
                            textAlign: 'left'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '22px',
                                borderBottom: '1px solid #f1f5f9',
                                paddingBottom: '16px'
                            }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.01em' }}>
                                        Add Section Below Catalog
                                    </h3>
                                    <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#64748b' }}>
                                        Select a classic template to insert below your product catalog grid.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowAddSectionPicker(false)}
                                    style={{
                                        background: '#f1f5f9',
                                        border: 'none',
                                        borderRadius: '8px',
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: '#64748b',
                                        transition: 'all 0.15s ease'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#0f172a'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                                    title="Close"
                                >
                                    <LuX size={18} />
                                </button>
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))',
                                gap: '16px'
                            }}>
                                {SHOP_SECTION_TEMPLATES.map(tmpl => {
                                    const Icon = tmpl.icon;
                                    return (
                                        <button
                                            key={tmpl.key}
                                            type="button"
                                            onClick={() => handleAddShopSectionTemplate(tmpl.key)}
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                padding: '18px 16px',
                                                background: '#ffffff',
                                                border: '1.5px solid #e2e8f0',
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                                                position: 'relative'
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.borderColor = tmpl.color;
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = `0 8px 20px -4px ${tmpl.color}25`;
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.borderColor = '#e2e8f0';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                        >
                                            <div style={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '10px',
                                                background: tmpl.bg,
                                                color: tmpl.color,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: '12px'
                                            }}>
                                                <Icon size={20} />
                                            </div>
                                            <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#0f172a', marginBottom: '4px' }}>
                                                {tmpl.name}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.45 }}>
                                                {tmpl.description}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Floating Button Styling Toolbar */}
            {showButtonStyling && (
                <ButtonStylingToolbar
                    settings={effectiveSettings}
                    update={update}
                    onClose={() => setShowButtonStyling(false)}
                />
            )}

            {/* Floating Admin Controls Bar */}
            {isAuthenticated && (
                <FloatingAdminBar
                    title="Shop"
                    badgeIcon="shop"
                    isEditing={isEditing}
                    saving={saving}
                    onEdit={handleStartEditing}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onCustomize={handleOpenCustomizer}
                    customizerLabel="Page Customizer"
                    customActions={
                        isEditing ? (
                            <>
                                <button
                                    type="button"
                                    onClick={handleAddProductLive}
                                    className="floating-admin-btn"
                                    style={{ background: '#2563eb', color: '#ffffff', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                    title="Add New Product"
                                >
                                    <LuPlus size={13} />
                                    <span>Add Product</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddSectionPicker(true);
                                        const pickerEl = document.getElementById('shop-add-section-container');
                                        if (pickerEl) {
                                            pickerEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        }
                                    }}
                                    className="floating-admin-btn"
                                    style={{
                                        background: 'rgba(99, 102, 241, 0.25)',
                                        border: '1px solid #6366f1',
                                        color: '#c7d2fe',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontWeight: '600'
                                    }}
                                    title="Add custom section below catalog grid"
                                >
                                    <LuPlus size={13} />
                                    <span>Add Section</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowUnpublishedInEdit(prev => !prev)}
                                    className="floating-admin-btn"
                                    style={{
                                        background: showUnpublishedInEdit ? 'rgba(59, 130, 246, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                                        border: showUnpublishedInEdit ? '1px solid #3b82f6' : '1px solid #ef4444',
                                        color: showUnpublishedInEdit ? '#93c5fd' : '#fca5a5',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontWeight: '600'
                                    }}
                                    title={showUnpublishedInEdit ? "Currently showing unpublished/draft items in live edit. Click to hide them." : "Currently hiding unpublished/draft items in live edit. Click to show them."}
                                >
                                    {showUnpublishedInEdit ? <LuEye size={13} /> : <LuEyeOff size={13} />}
                                    <span>{showUnpublishedInEdit ? `Unpublished: Visible (${draftProductCount + hiddenCatCount})` : 'Unpublished: Hidden'}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowButtonStyling(!showButtonStyling)}
                                    className="floating-admin-btn floating-admin-btn-ghost"
                                    title="Customize button colors, styling and CSS"
                                    style={{
                                        background: showButtonStyling ? 'rgba(59, 130, 246, 0.25)' : undefined,
                                        borderColor: showButtonStyling ? '#3b82f6' : undefined,
                                        color: showButtonStyling ? '#60a5fa' : undefined
                                    }}
                                >
                                    <LuPalette size={13} />
                                    <span>Buttons</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const currentVal = styleSettings.shopCardShowOptions !== false && styleSettings.shopCardShowOptions !== 'false';
                                        handleStyleChange('shopCardShowOptions', !currentVal);
                                    }}
                                    className="floating-admin-btn"
                                    style={{
                                        background: (styleSettings.shopCardShowOptions !== false && styleSettings.shopCardShowOptions !== 'false') ? 'rgba(16, 185, 129, 0.25)' : 'rgba(148, 163, 184, 0.25)',
                                        border: (styleSettings.shopCardShowOptions !== false && styleSettings.shopCardShowOptions !== 'false') ? '1px solid #10b981' : '1px solid #94a3b8',
                                        color: (styleSettings.shopCardShowOptions !== false && styleSettings.shopCardShowOptions !== 'false') ? '#6ee7b7' : '#cbd5e1',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        fontWeight: '600'
                                    }}
                                    title="Toggle showing variation and option dropdowns on product grid cards"
                                >
                                    <span>⚙️ Card Options: {(styleSettings.shopCardShowOptions !== false && styleSettings.shopCardShowOptions !== 'false') ? 'Shown' : 'Hidden'}</span>
                                </button>
                            </>
                        ) : null
                    }
                    storageKey="omnivaso_shop_admin_bar_minimized"
                />
            )}

            {/* =========================================================================
              * 🛡️ ARCHITECTURAL PRESERVATION GUARD — FOOTER (<SiteFooter>)
              * DO NOT ALTER, RENAME PROPS, OR BYPASS THIS GLOBAL FOOTER.
              * MUST PASS ALL PROPS (Editable, onUpdate, onSetConfig, onSaveConfig, onAddCol, etc.)
              * AND REMAIN 100% IDENTICAL WITH ArticlesPage, LandingPage, ProductPage, etc.
              * ========================================================================= */}
            <SiteFooter
                footer={data?.footer || footer}
                socials={data?.socials || socials}
                settings={data?.settings || settings}
                config={data}
                typography={data?.typography || typography}
                getFontStyles={_getFontStyles}
                getSideBorder={_getSideBorder}
                getShadow={_getShadow}
                Editable={Editable}
                isAuthenticated={isAuthenticated}
                isEditing={isEditing}
                onUpdate={update}
                onSetConfig={setData}
                onSaveConfig={onSaveConfig}
                onAddCol={addFooterCol}
                onRemoveCol={removeFooterCol}
                onMoveCol={moveFooterCol}
            />

            {/* Customizer Drawer */}
            <ShopCustomizerDrawer
                isOpen={showCustomizer}
                isAuthenticated={isAuthenticated}
                customizerTab={customizerTab}
                setCustomizerTab={setCustomizerTab}
                styleSettings={styleSettings}
                handleStyleChange={handleStyleChange}
                setStyleSettings={setStyleSettings}
                settings={settings}
                update={update}
                handleCancelStyles={handleCancelStyles}
                handleResetStyles={handleResetStyles}
                handleSaveStyles={handleSaveStyles}
                categories={categories}
                dbCategories={dbCategories}
                handleShuffleCategories={handleShuffleCategories}
                handleReorderCategories={handleReorderCategories}
                handleMoveCategory={handleMoveCategory}
                handleSortCategoriesAlpha={handleSortCategoriesAlpha}
                openTrigger={customizerTrigger}
                isSimpleMode={isSimpleMode}
                setIsSimpleMode={setIsSimpleMode}
                selectedSimpleSection={selectedSimpleSection}
                setSelectedSimpleSection={setSelectedSimpleSection}
                onSectionSelect={handleSectionSelect}
                showUnpublishedInEdit={showUnpublishedInEdit}
                setShowUnpublishedInEdit={setShowUnpublishedInEdit}
                draftProductCount={draftProductCount}
                hiddenCatCount={hiddenCatCount}
            />

            {/* Product Expanded Edit Modal */}
            <ProductExpandedEditModal
                isOpen={Boolean(expandedProductModal)}
                onClose={() => setExpandedProductModal(null)}
                product={expandedProductModal}
                categories={dbCategories}
                onSave={(updatedProd) => {
                    setProducts(prev => prev.map(p => p.id === updatedProd.id ? updatedProd : p));
                }}
                primaryColor={primaryColor}
            />
        </div>
    );
};

export default ShopPage;
